/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { yupResolver } from '@hookform/resolvers/yup';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  LegalRegion,
  Ps,
  PsDerivation,
  PsDerivationContent,
  PsDerivationJudgement,
  PsLegalInformation,
  PsLegalRegion,
  PsTextCondition,
  PsTextConditionDrawing,
  PsTextConditionFlist,
  PsTouchup,
  PsTouchupCandidate,
  PsTouchupTarget,
  PsTreeItem,
  PsVersionHistory,
} from '../../../../api/model';
import { usePostPsAdd, usePostPsEdit } from '../../../../api/pss/pss';
import { PsContext } from '../../../../contexts/PsContext';
import { getAxiosRequestConfig } from '../../../../utils/api';
import { getNextId } from '../../../../utils/id';
import { toString } from '../../../../utils/string';
import { CheckBox } from '../../../atoms/CheckBox';
import ScrollToHash from '../../../atoms/ScrollToHash';
import SingleSelect from '../../../atoms/SingleSelect';
import { TextInput } from '../../../atoms/TextInput';
import { useEditModeSetValue } from '../../../layouts/LoginedLayout';
import ConfirmModal from '../../../modules/ConfirmModal';
import DraggableModelessDialogsProvider from '../../../modules/DraggableModelessDialogsProvider';
import ErrorModal from '../../../modules/ErrorModal';
import OkModal from '../../../modules/OkModal';
import SlideToggle from '../../../modules/SlideToggle';
import SpinnerModal from '../../../modules/SpinnerModal';
import PsStateHistoryDialog from '../index/Dialog/PsStateHistoryDialog';
import PsAttributes from './PsAttributes';
import PsDerivationRow from './PsDerivationRow';
import PsDiffViewerDialog from './PsDiffViewerDialog';
import PsLegalRegionSelectDialog from './PsLegalRegionSelectDialog';
import PsTextConditionAddEditDialog from './PsTextConditionAddEditDialog';
import PsTouchupAddEditDialog from './PsTouchupAddEditDialog';
import { DiffType, PsDetailsMode, PsDiffMode } from './constants/types';
import { PsSchemaType, psSchema } from './constants/variables';
import { getPsTreeItem } from './tree/PsTree';

/** PsDetails と PsGetter で共通の Props。 */
export interface PsDetailsPsGetterCommonProps {
  /** PS 閲覧・追加・編集・比較の内、どのモードかを示す。 */
  mode: PsDetailsMode;

  /** PS 情報タブ モード。 */
  psInfoTabMode?: boolean | undefined;

  /** PS 情報タブ モードで使用。ラジオ ボタンで派生を選択時に呼び出される関数。引数に選択された派生の ID を渡す。 */
  setSelectedDerivationId?: ((selectedDerivationId: number | undefined) => void) | undefined;

  /** PS 情報タブ モードで使用。派生IDを指定された場合は初期表示時にラジオボタンを選択状態にする。 */
  selectedDerivationId?: number | undefined; // eslint-disable-line

  /** 対象となる PS か否かを示す。 */
  isTargetPs?: boolean | undefined;

  /** PsDetailsPage の state 経由で他のコンポーネントに渡すための state 設定関数。 */
  setTargetPs?: ((ps: Ps) => void) | undefined;

  /** PS ツリーを再検索 (postPsTreeSearch) するか設定する関数。 */
  setDoRePostPsTreeSearch?: ((doRePostPsTreeSearch: boolean) => void) | undefined;

  /** PS ツリーのドラッグ & ドロップで変更されたルートの PsTreeItem。 */
  modifiedRootPsTreeItem?: PsTreeItem | undefined;

  /** 一括変更用の表示言語の state。0: 日本語、1: 米語、2: 英語。 */
  bulkDisplayLanguages?: boolean[];
}

interface Props extends PsDetailsPsGetterCommonProps {
  /** このコンポーネントで表示・編集する対象の PS。 */
  ps: Ps;

  /** PS 比較で使用する。比較対象となる、旧バージョンの PS。 */
  olderPs?: Ps | undefined;

  /** 差分モードの種別(差分|追加|削除) */
  diffMode?: PsDiffMode | undefined;
}

/** PS 詳細情報。 */
function PsDetails({
  mode,
  psInfoTabMode,
  setSelectedDerivationId,
  selectedDerivationId,
  ps,
  olderPs,
  isTargetPs,
  setTargetPs,
  setDoRePostPsTreeSearch,
  modifiedRootPsTreeItem,
  bulkDisplayLanguages,
  diffMode,
}: Props) {
  // i18n 用。
  const { t, i18n } = useTranslation();

  /** PS 追加・編集画面 → 他の画面へ遷移時にワーニングを表示するか否か設定する関数。 */
  const setEditMode = useEditModeSetValue();
  // ↑ データが変更されたら true とする。

  const navigate = useNavigate();

  // const defaultOptions = useDefaultOptions(i18n.language);

  const axiosConfig = getAxiosRequestConfig(i18n.language);

  const options = {
    axios: axiosConfig,
  };

  /** postPsAdd API。 */
  const postPsAddMutation = usePostPsAdd(options);

  /** postPsEdit API。 */
  const postPsEditMutation = usePostPsEdit(options);

  /** PS ツリーの更新をしてもよいか否か。 */
  const [canSetDoRePostPsTreeSearch, setCanSetDoRePostPsTreeSearch] = useState<boolean>(false);

  /** PS 情報タブ モードで使用 */
  const [psInfoTabSelectId, setPsInfoTabSelectId] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (selectedDerivationId !== undefined) {
      setPsInfoTabSelectId(String(selectedDerivationId));
    }
  }, [setPsInfoTabSelectId, selectedDerivationId]);

  useEffect(() => {
    if (
      // 改廃案保存成功時のみ PS ツリーを更新。
      (postPsAddMutation.isSuccess || postPsEditMutation.isSuccess) &&
      canSetDoRePostPsTreeSearch
    ) {
      if (typeof setDoRePostPsTreeSearch === 'function') {
        // PS ツリーを更新。
        setDoRePostPsTreeSearch(true);
        // 更新したので、次の handleSubmit 実行時まで更新を不許可に。
        setCanSetDoRePostPsTreeSearch(false);
      }
    }
  }, [canSetDoRePostPsTreeSearch, postPsAddMutation.isSuccess, postPsEditMutation.isSuccess, setDoRePostPsTreeSearch]);

  const getDefaultValues = useCallback((freshPs: Ps) => {
    // Schema から検索条件のデフォルト値を取得し、引数の Ps で上書きする。
    const defaultValues = {
      ...psSchema.getDefault(),
      // Schema では text_type などは .default(null) だが、<option value=''>選択してください</option> を選択するために、ここで '' にする。
      text_type: '',
      content_type: '',
      // null 値の properties を削除 (undefined になる) したものを渡す事で、デフォルト値が適切になるようにする。null のままだと、↑ の text_type 等を null で上書きしてしまい、正しい <option> が選択されない。
      ...getNullPropertiesDeletedObject(freshPs),
    };

    if (Number(defaultValues.type) > 1) {
      // ルート PS と章 PS 以外の場合。

      if (!Array.isArray(defaultValues.legal_informations) || defaultValues.legal_informations.length < 3) {
        // legal_informations が存在しない場合、デフォルト値として言語分の PsLegalInformation を設定する。
        defaultValues.legal_informations = [0, 1, 2].map((languageId: number, index: number) => ({
          // key なので unique な値にする。
          legal_information_id: index,
          verbatim: false,
          verbatim_tag: false,
          information: null,
          note: null,
          legal_regions: [],
        }));
      }

      if (!Array.isArray(defaultValues.webom_descriptions) || defaultValues.webom_descriptions.length < 3) {
        // webom_descriptions が存在しない場合、デフォルト値として言語分の string を設定する。
        defaultValues.webom_descriptions = ['', '', ''];
      }

      if (!Array.isArray(defaultValues.dom_descriptions) || defaultValues.dom_descriptions.length < 3) {
        // dom_descriptions が存在しない場合、デフォルト値として言語分の string を設定する。
        defaultValues.dom_descriptions = ['', '', ''];
      }

      if (!Array.isArray(defaultValues.dom_search_keywords) || defaultValues.dom_search_keywords.length < 3) {
        // dom_search_keywords が存在しない場合、デフォルト値として言語分の string を設定する。
        defaultValues.dom_search_keywords = ['', '', ''];
      }
    }

    return defaultValues as PsSchemaType;
  }, []);

  // const defaultValues = {
  //   ...psSchema.getDefault(),
  //   // Schema では text_type などは .default(null) だが、<option value=''>選択してください</option> を選択するために、ここで '' にする。
  //   text_type: '',
  //   content_type: '',
  //   // null 値の properties を削除 (undefined になる) したものを渡す事で、デフォルト値が適切になるようにする。null のままだと、↑ の text_type 等を null で上書きしてしまい、正しい <option> が選択されない。
  //   ...getNullPropertiesDeletedObject(ps),
  // };

  // if (
  //   // ルート PS と章 PS 以外の場合。
  //   (Number(defaultValues.type) > 1)
  //   &&
  //   (
  //     (!(Array.isArray(defaultValues.legal_informations)))
  //     ||
  //     (defaultValues.legal_informations.length < 3)
  //   )
  // ) {
  //   // legal_informations が存在しない場合、デフォルト値として言語分の PsLegalInformation を設定する。
  //   defaultValues.legal_informations = [0, 1, 2].map(
  //     (languageId: number, index: number) =>
  //     ({
  //       // key なので unique な値にする。
  //       legal_information_id: index,
  //       verbatim: false,
  //       verbatim_tag: false,
  //       information: null,
  //       note: null,
  //       legal_regions: [],
  //     })
  //   );
  // }

  const defaultValues = useMemo(() => getDefaultValues(ps), [getDefaultValues, ps]);

  // React Hook Form。
  const useFormReturnProps = useForm({
    defaultValues,
    resolver: yupResolver(
      psSchema
      // psTreeSearchConditionsSchema.concat(
      //   yup.object().shape({
      //     // half() と date() は /src/frontend/app/src/index.tsx の yup.addMethod 時に追加されるため、ここで定義し直す。
      //     [psTreeSearchConditionsPropertyNames[14]]: yup.array().of(yup.string().half()).default([]),
      //     [psTreeSearchConditionsPropertyNames[23]]: yup.string().date().default(''),
      //     [psTreeSearchConditionsPropertyNames[24]]: yup.string().date().default(''),
      //   })
      // )
    ),
    criteriaMode: 'all',
    mode: 'all',
  });

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    watch,
    register,
    formState,
    // resetField,
    reset,
  } = useFormReturnProps;

  useEffect(() => {
    // if (
    //   postPsEditMutation.isSuccess
    //   &&
    //   postPsEditMutation.data?.data
    //   // TODO?: state か ref で、成功時の一回のみ reset するようにするべきか。
    //   &&
    //   (!(_.isEqual(getDefaultValues(postPsEditMutation.data.data), getValues())))
    // ) {
    //   // PS 編集 (改廃案保存) 成功時、form values を更新。
    //   reset(getDefaultValues(postPsEditMutation.data.data));
    // }
    // else
    if (
      !postPsAddMutation.isLoading &&
      !postPsAddMutation.isSuccess &&
      !postPsEditMutation.isLoading &&
      !postPsEditMutation.isSuccess
    ) {
      const values = getValues();
      if (defaultValues.parent_ps_version_id !== values.parent_ps_version_id) {
        // 右クリックメニューの「追加」選択時、右ペインに詳細表示中のPSと、右クリックで選択したPSが異なる場合、パラメータpsの親PSバージョンIDがフォームと異なるため、フォームに反映する必要がある。
        reset(defaultValues);
      }
    }
  }, [
    defaultValues,
    getValues,
    postPsAddMutation.isLoading,
    postPsAddMutation.isSuccess,
    postPsEditMutation.isLoading,
    postPsEditMutation.isSuccess,
    reset,
  ]);

  // console.log('ps', ps);
  // console.log('getDefaultValues(ps)', getDefaultValues(ps));
  // console.log("getValues('parent_ps_version_id')", getValues('parent_ps_version_id' as never));
  // // console.log("watch('parent_ps_version_id')", watch('parent_ps_version_id' as never));
  // console.log("getValues('ps_version_id')", getValues('ps_version_id' as never));

  // derivations 用。
  const {
    fields: derivationFields,
    append: appendDerivation,
    // insert: insertDerivation,
    remove: removeDerivation,
    update: updateDerivation,
    swap: swapDerivations,
  } = useFieldArray({
    control,
    name: 'derivations' as never,
  });

  // text_conditions 用。
  const {
    // fields: textConditionFields,
    append: appendTextCondition,
    remove: removeTextCondition,
    insert: insertTextCondition,
    // swap: swaptext_conditions,
  } = useFieldArray({
    control,
    name: 'text_conditions' as never,
  });

  // touchups 用。
  const {
    // fields: touchupFields,
    append: appendTouchup,
    remove: removeTouchup,
    // insert: insertTouchup,
  } = useFieldArray({
    control,
    name: 'touchups' as never,
  });

  /** watch する derivations。 */
  const derivations = useWatch({
    control,
    name: `derivations` as never,
  }) as PsDerivation[];
  // const derivations = watch(`derivations`);

  /** watch する text_conditions。 */
  const textConditions = useWatch({
    control,
    name: `text_conditions` as never,
  }) as PsTextCondition[];

  /** watch する touchups。 */
  const touchups = watch(`touchups` as never) as unknown as PsTouchup[];

  /** watch する legal_informations。 */
  const legalInformations = watch(`legal_informations` as never) as unknown as PsLegalInformation[];

  /** watch する legal_regions。言語毎の配列。 */
  const legalRegionsArray = [0, 1, 2].map(
    (languageId: number, index: number) =>
      watch(`legal_informations.${index}.legal_regions` as never) as unknown as PsLegalRegion[]
  );

  /** 次の ID 生成のためにテキスト確定条件の ID の配列を取得。 */
  const textConditionIds = useMemo(
    () =>
      Array.isArray(textConditions)
        ? textConditions.map((psTextCondition: PsTextCondition) =>
            typeof psTextCondition.text_condition_id === 'number' ? psTextCondition.text_condition_id : 0
          )
        : [],
    [textConditions]
  );

  // データが変更されたら、画面遷移時にワーニングを表示するよう設定する。
  useEffect(() => {
    setEditMode(formState.isDirty);
  }, [formState.isDirty, setEditMode]);

  useEffect(() => {
    if (bulkDisplayLanguages !== undefined) {
      setDisplayLanguages(bulkDisplayLanguages);
    }
  }, [bulkDisplayLanguages]);

  // state。

  /** 表示言語の state。0: 日本語、1: 米語、2: 英語。 */
  const [displayLanguages, setDisplayLanguages] = useState<boolean[]>([true, true, true]);

  /** 表示切替ボタンの領域の展開・格納状態。 */
  const [isSwitchDisplayButtonsAreaOpen, setIsSwitchDisplayButtonsAreaOpen] = useState<boolean>(false);

  /** 派生文書のみ表示。 */
  const [doShowDerivationsOnly, setDoShowDerivationsOnly] = useState<boolean>(false);

  /** 判断情報の展開・格納状態。 */
  const [isJudgementInfoAreaOpen, setIsJudgementInfoAreaOpen] = useState<boolean>(true);

  /** タッチアップ情報の展開・格納状態。 */
  const [isTouchupInfoAreaOpen, setIsTouchupInfoAreaOpen] = useState<boolean>(true);

  /** 管理情報の展開・格納状態。 */
  const [isManagementInfoAreaOpen, setIsManagementInfoAreaOpen] = useState<boolean>(true);

  /** 法規情報の展開・格納状態。 */
  const [isLegalInfoAreaOpen, setIsLegalInfoAreaOpen] = useState<boolean>(true);

  /** 他媒体情報の展開・格納状態。 */
  const [isOtherMediaInfoAreaOpen, setIsOtherMediaInfoAreaOpen] = useState<boolean>(true);

  /** 履歴情報の展開・格納状態。 */
  const [isVersionsInfoAreaOpen, setIsVersionsInfoAreaOpen] = useState<boolean>(true);

  /** 変更箇所情報の展開・格納状態。 */
  const [isDifferencesInfoAreaOpen, setIsDifferencesInfoAreaOpen] = useState<boolean>(true);

  /** PS 状態履歴ダイアログに渡すバージョン ID。< 0 でダイアログ非表示、>= 0 でダイアログ表示。 */
  const [psStateHistoryDialogVersionId, setPsStateHistoryDialogVersionId] = useState<number>(-1);

  /** 削除する派生の index。< 0 なら派生削除の確認モーダルを表示しない (閉じる)。>= 0 なら表示して、OK ならその index の派生を削除する。 */
  const [psDerivationIndexToRemove, setPsDerivationIndexToRemove] = useState<number>(-1);

  /** Diffダイアログの表示／非表示 */
  const [isDiffDlgOpen, setIsDiffDlgOpen] = useState(false);

  /** Diffタイトル */
  const [diffTitle, setDiffTitle] = useState('');

  /** Diffダイアログの言語ID */
  const [languageId, setLanguageId] = useState(-1);

  /** 現在のPS */
  const [psValue, setPsValue] = useState<string | object | undefined>('');

  /** 一つ前のPS */
  const [psOldValue, setPsOldValue] = useState<string | object | undefined>('');

  /**
   * テキスト確定条件追加編集ダイアログ表示モードと index。
   * 'Add:[index]': 新規追加、'Edit:[index]': 編集、undefined: 非表示。
   * [index] は、操作対象となる text_condition の text_conditions 上の index。
   */
  const [psTextConditionAddEditDialogOpenModeAndIndex, setPsTextConditionAddEditDialogOpenModeAndIndex] = useState<
    string | undefined
  >(undefined);

  /** 削除するテキスト確定条件の index。< 0 ならテキスト確定条件削除の確認モーダルを表示しない (閉じる)。>= 0 なら表示して、OK ならその index のテキスト確定条件を削除する。 */
  const [psTextConditionIndexToRemove, setPsTextConditionIndexToRemove] = useState<number>(-1);

  /** テキスト確定条件が追加か削除されたかを判断するため、以前の length を保存しておく。 */
  const [previousPsTextConditionsLength, setPreviousPsTextConditionsLength] = useState<number>(
    Array.isArray(textConditions) ? textConditions.length : 0
  );

  /** 抜粋ステータスIDから抜粋ステータス名称を取得する。 */
  const getExcerptStatusText = (id: number | undefined) =>
    id === 0 ? t('未指定') : id === 1 ? t('非対象') : t('対象');

  /** SingleSelect に渡す関数。 */
  const getValue = (valueLabelPair: string) => valueLabelPair.split(':')[0];
  /** SingleSelect に渡す関数。 */
  const getLabel = (valueLabelPair: string) => valueLabelPair.split(':')[1];

  // console.log('previousPsTextConditionsLength', previousPsTextConditionsLength);
  // console.log('textConditionFields.length', textConditionFields.length);
  // console.log('textConditions.length', (textConditions as PsTextCondition[]).length);

  /** 次の ID 生成のために PS 派生判定の ID の配列を取得。 */
  const psDerivationJudgementIds: number[] = useMemo(
    () =>
      Array.isArray(derivations)
        ? (derivations
            .map((psDerivation: PsDerivation) => {
              if (Array.isArray(psDerivation.derivation_judgements)) {
                return psDerivation.derivation_judgements.map((psDerivationJudgement: PsDerivationJudgement) =>
                  typeof psDerivationJudgement.derivation_judgement_id === 'number'
                    ? psDerivationJudgement.derivation_judgement_id
                    : 0
                );
              }
              return [];
            })
            .flat(Infinity) as number[])
        : [],
    [derivations]
  );

  // console.log('derivations: ', derivations);
  // console.log('psDerivationJudgementIds: ', psDerivationJudgementIds);

  // const {
  //   getNextId: getNextPsDerivationJudgementId,
  // } = useNextId(psDerivationJudgementIds);

  useEffect(() => {
    if (mode !== 'Diff') {
      // PS 比較画面以外の場合。
      if (previousPsTextConditionsLength !== textConditions.length) {
        // テキスト確定条件の配列の長さが変わっていた場合 (追加 / 削除の結果)。
        if (Array.isArray(derivations)) {
          derivations.forEach((psDerivation: PsDerivation, psDerivationIndex: number) => {
            if (Array.isArray(textConditions)) {
              const newDerivationJudgements = textConditions.map((psTextCondition: PsTextCondition) => {
                // 現在のテキスト確定条件を元に判断。

                let foundOrNewPsDerivationJudgement;

                if (Array.isArray(psDerivation.derivation_judgements)) {
                  // 現在のテキスト確定条件に対応する PS 派生判定を取得。
                  // 削除されたテキスト確定条件に対応するものは省かれることになる。
                  foundOrNewPsDerivationJudgement = psDerivation.derivation_judgements.find(
                    (psDerivationJudgement: PsDerivationJudgement) =>
                      psDerivationJudgement.ps_text_condition_id === psTextCondition.text_condition_id
                  );
                }

                if (!foundOrNewPsDerivationJudgement) {
                  // 次の ID を生成して追加。
                  psDerivationJudgementIds.push(getNextId(psDerivationJudgementIds));

                  // PS 派生判定が見つからなかった (もしくは psDerivation.derivation_judgements が配列ではなかった) 場合。
                  // 新規追加されたテキスト確定条件に対応する PS 派生判定を追加する。
                  foundOrNewPsDerivationJudgement = {
                    // 次に追加可能な ID を設定。
                    derivation_judgement_id: psDerivationJudgementIds[psDerivationJudgementIds.length - 1],
                    // テキスト確定条件に対応。
                    ps_text_condition_id: psTextCondition.text_condition_id,
                    // デフォルトは 0 (表示は '-') とする。
                    value: 0,
                  };
                }

                return foundOrNewPsDerivationJudgement;
              });

              // JSON の stringify と parse でもいいが。
              // 新しい PsDerivation を作成。
              const newPsDerivation = _.cloneDeep(psDerivation);

              // derivation_judgements を置き換え。
              newPsDerivation.derivation_judgements = newDerivationJudgements;

              // console.log('newPsDerivation: ', newPsDerivation);

              // 派生を更新。
              updateDerivation(psDerivationIndex, newPsDerivation);
            }
          });
        }
      }

      if (previousPsTextConditionsLength !== textConditions.length) {
        // 比較用に保存。
        setPreviousPsTextConditionsLength(Array.isArray(textConditions) ? textConditions.length : 0);
      }
    }
  }, [derivations, mode, previousPsTextConditionsLength, psDerivationJudgementIds, textConditions, updateDerivation]);

  /**
   * タッチアップ追加編集ダイアログ表示モードと index。
   * 'Add:[index]': 新規追加、'Edit:[index]': 編集、undefined: 非表示。
   * [index] は、操作対象となる touchup の touchups 上の index。
   */
  const [psTouchupAddEditDialogOpenModeAndIndex, setPsTouchupAddEditDialogOpenModeAndIndex] = useState<
    string | undefined
  >(undefined);

  /** 削除するタッチアップの index。< 0 ならタッチアップ削除の確認モーダルを表示しない (閉じる)。>= 0 なら表示して、OK ならその index のタッチアップを削除する。 */
  const [psTouchupIndexToRemove, setPsTouchupIndexToRemove] = useState<number>(-1);

  // /** タッチアップ追加編集ダイアログ表示・非表示 state。 */
  // const [isPsTouchUpAddEditDialogOpen, setIsPsTouchUpAddEditDialogOpen] = useState<boolean>(false);

  /** 法規適用国指定ダイアログで「選択」ボタン押下時に設定する legal_regions の言語の ID。
   * 0: 日本語、1: 米語、2: 英語。
   * < 0 でダイアログ非表示。>= 0 で表示。
   * */
  const [psLegalRegionLanguageId, setPsLegalRegionLanguageId] = useState<number>(-1);

  useEffect(() => {
    if (isTargetPs) {
      if (typeof setTargetPs === 'function') {
        // 選択された PS の場合、PsDetailsPage の state を設定する。
        setTargetPs(ps);
      }
    }
  }, [ps, isTargetPs, setTargetPs]);

  /** handleSubmit() の SubmitErrorHandler が呼ばれた時、その引数を set する。undefined でない場合、モーダルで内容を表示する。 */
  const [handleSubmitErrors, setHandleSubmitErrors] = useState<unknown>(undefined);

  // console.log('olderPs: ', olderPs);
  // console.log('ps: ', ps);

  // start: PS 比較画面関連。

  // PS 詳細情報については、新旧 PS データで結果が異なる部分を、追加・変更された情報は黄色、 削除された情報は赤色でハイライト表示する。

  // 派生、テキスト確定条件、タッチアップ項目は、PS バージョンと紐づいているため、バージョンが変わると別の ID が採番される仕様である。そのため、この 3 つのテーブルそれぞれに固有 ID (unique_id) フィールドを追加し、バージョン間でも対応が取れるようにした。

  // PS 詳細情報の追加・削除: 派生、テキスト確定条件、タッチアップについて、固有 ID が同じものを比較し、旧バージョンにあって新バージョンにない固有 ID のデータは削除、旧バージョンになくて新バージョンにある固有 ID のデータは追加、という判別をする。
  // 変更: 固有 ID が同じものを比較した結果、同じ ID のデータがある場合は、その中身を比較して変更があれば変更ありとする。

  /** 比較を実行したか否か。useEffect() → set state の無限ループ防止用。 */
  const isDiffedRef = useRef(false);
  // When Strict Mode is on, in development, React runs setup and cleanup one extra time before the actual setup.
  // ↑ 2 回実行時、state は更新されないようだ。なので state ではなく ref を使って判断する。

  // ここで比較し、ID と DiffType の Map を作り、削除されていたものについては append() などで form value としてマージしつつ、ここおよび配下のそれぞれのコンポーネントで Map を参照して背景色を変える。

  /** PsTextCondition.text_condition_id と DiffType の Map。 */
  const psTextConditionIdDiffTypeMapRef = useRef(new Map<number, DiffType>());

  /** PsDerivationContent.derivation_content_id と DiffType の Map。 */
  const psDerivationContentIdDiffTypeMapRef = useRef(new Map<number, DiffType>());
  /** PsDerivationJudgement.derivation_judgement_id と DiffType の Map。 */
  const psDerivationJudgementIdDiffTypeMapRef = useRef(new Map<number, DiffType>());

  /** PsTouchup.touchup_id と DiffType の Map。touch_up 用。 */
  const psTouchupIdTouchUpDiffTypeMapRef = useRef(new Map<number, DiffType>());
  /** PsTouchup.touchup_id と DiffType の Map。judgement_information 用。 */
  const psTouchupIdJudgementInformationDiffTypeMapRef = useRef(new Map<number, DiffType>());
  /** PsTouchupTarget.touchup_target_id と DiffType の Map。target 用。 */
  const psTouchupTargetIdTargetDiffTypeMapRef = useRef(new Map<number, DiffType>());
  /** PsTouchupTarget.touchup_target_id と DiffType の Map。touchup_candidates 用。 */
  const psTouchupTargetIdTouchupCandidatesDiffTypeMapRef = useRef(new Map<number, DiffType>());

  /** PsDerivationContent.derivation_content_id と xml の Map。 */
  const psDerivationContentIdDiffXmlMapRef = useRef(new Map<number, string>());

  /** PsDerivationJudgement.derivation_judgement_id と value の Map。 */
  const psDerivationJudgementIdDiffValueMapRef = useRef(new Map<number, number>());

  // const [psDerivationContentIdDiffTypeMap, setPsDerivationContentIdDiffTypeMap] = useState<Map<number, DiffType>>(new Map<number, DiffType>());
  // console.log('psDerivationContentIdDiffTypeMap:', psDerivationContentIdDiffTypeMap);

  // const [psDerivationJudgementIdDiffTypeMap, setPsDerivationJudgementIdDiffTypeMap] = useState<Map<number, DiffType>>(new Map<number, DiffType>());
  // console.log('psDerivationJudgementIdDiffTypeMap:', psDerivationJudgementIdDiffTypeMap);

  // ここで比較し、ID と DiffType の Map を作り、削除されていたものについては ps の方にマージしたデータを作り、ここおよび配下のそれぞれのコンポーネントで Map を参照して背景色を変える。

  // /** 削除されていたテキスト確定条件 (複数かも) と、新バージョンのテキスト確定条件の配列をマージした配列。 */
  // const mergedPsTextConditions: PsTextCondition[] = (Array.isArray(textConditions)) ? [...textConditions] : [];

  // /** 削除されていた派生 (複数かも) と、新バージョンの派生の配列をマージした配列。 */
  // const mergedPsDerivations: PsDerivation[] = (Array.isArray(derivations)) ? [...derivations] : [];

  /** PsDerivationContent.derivation_content_id と DiffType の Map。 */
  // const psDerivationContentIdDiffTypeMap = new Map<number, DiffType>();

  /** PsDerivationJudgement.derivation_judgement_id と DiffType の Map。 */
  // const psDerivationJudgementIdDiffTypeMap = new Map<number, DiffType>();

  // // olderPs は PS 比較画面の時のみ prop で渡される。
  // if ((mode === 'Diff') && olderPs) {

  //   // テキスト確定条件を比較。
  //   if (ps.text_conditions && olderPs.text_conditions) {

  //     ps.text_conditions.forEach(
  //       (psTextCondition: PsTextCondition) => {
  //         // とりあえず追加されたものとして Map に登録しておく。
  //         psTextConditionIdDiffTypeMap.set(psTextCondition.text_condition_id as number, 'Added');

  //         /** olderPs.text_conditions の中から、同じ unique_id を持つものを取得。 */
  //         const olderPsTextConditionSameUniqueId = (olderPs.text_conditions as PsTextCondition[]).find(
  //           (olderPsTextCondition: PsTextCondition) =>
  //             (olderPsTextCondition.unique_id === psTextCondition.unique_id)
  //         );

  //         if (olderPsTextConditionSameUniqueId) {
  //           // 同じ unique_id を持つものが見つかったら内容を比較。
  //           if (
  //             (psTextCondition.text_condition === olderPsTextConditionSameUniqueId.text_condition)
  //             &&
  //             // text_condition_drawings を比較。
  //             (comparePsTextConditionDrawings(olderPsTextConditionSameUniqueId.text_condition_drawings, psTextCondition.text_condition_drawings))
  //             &&
  //             // text_condition_flists を比較。
  //             (comparePsTextConditionFlists(olderPsTextConditionSameUniqueId.text_condition_flists, psTextCondition.text_condition_flists))
  //             &&
  //             (psTextCondition.service_spec_table === olderPsTextConditionSameUniqueId.service_spec_table)
  //             &&
  //             (psTextCondition.tos === olderPsTextConditionSameUniqueId.tos)
  //             &&
  //             (psTextCondition.tos_supplement === olderPsTextConditionSameUniqueId.tos_supplement)
  //             &&
  //             (psTextCondition.hg_check_require === olderPsTextConditionSameUniqueId.hg_check_require)
  //           ) {
  //             // 変更が無い。
  //             // 'Added' で登録していたのを 'NoChange' で上書き。
  //             psTextConditionIdDiffTypeMap.set(
  //               psTextCondition.text_condition_id as number,
  //               'NoChange'
  //             );
  //           }
  //           else {
  //             // 変更があった。
  //             // 'Added' で登録していたのを 'Modified' で上書き。
  //             psTextConditionIdDiffTypeMap.set(
  //               psTextCondition.text_condition_id as number,
  //               'Modified'
  //             );
  //           }
  //         }
  //       }
  //     );

  //     // 削除されていたテキスト確定条件を、新バージョンのテキスト確定条件の配列とマージ。
  //     olderPs.text_conditions.forEach(
  //       (olderPsTextCondition: PsTextCondition) => {
  //         // ps.text_conditions の中から、olderPsTextCondition と同じ unique_id を持つものを探す。
  //         if ((ps.text_conditions as PsTextCondition[]).every(
  //           (psTextCondition: PsTextCondition) =>
  //             (psTextCondition.unique_id !== olderPsTextCondition.unique_id)
  //         )) {
  //           // 同じ unique_id を持つものが ps.text_conditions に見つからなかった場合。

  //           // 削除されていたテキスト確定条件を新バージョンのテキスト確定条件の配列に追加。
  //           mergedPsTextConditions.push(olderPsTextCondition);

  //           // 削除されていたものとして Map に登録。
  //           psTextConditionIdDiffTypeMap.set(olderPsTextCondition.text_condition_id as number, 'Deleted');
  //         }
  //       }
  //     );
  //   }

  //   // 派生を比較。
  //   if (ps.derivations && olderPs.derivations) {

  //     ps.derivations.forEach(
  //       (psDerivation: PsDerivation) => {
  //         // derivations を比較。

  //         /** olderPs.derivations の中から、同じ unique_id を持つものを取得。 */
  //         const olderPsDerivationSameUniqueId = (olderPs.derivations as PsDerivation[]).find(
  //           (olderPsDerivation: PsDerivation) =>
  //             (olderPsDerivation.unique_id === psDerivation.unique_id)
  //         );

  //         // derivation_contents を比較。
  //         if (psDerivation.derivation_contents) {
  //           psDerivation.derivation_contents.forEach(
  //             (psDerivationContent: PsDerivationContent, psDerivationContentIndex: number) => {
  //               // とりあえず、追加されたものとしておく。
  //               // 以下の場合に対応:
  //               // ・olderPs.derivations に無い PsDerivation が ps.derivations にある → derivation_content も追加されたものとする。
  //               // olderPsDerivationSameUniqueId.derivation_contents が配列じゃない (可能性は無いはず)。
  //               // olderPsDerivationSameUniqueId.derivation_contents の中から、同じ位置のものが見つからない (可能性は無いはず)。
  //               psDerivationContentIdDiffTypeMap.set(
  //                 psDerivationContent.derivation_content_id as number,
  //                 'Added'
  //               );

  //               if (olderPsDerivationSameUniqueId) {
  //                 // 同じ unique_id を持つものが見つかった。

  //                 if (olderPsDerivationSameUniqueId.derivation_contents) {
  //                   /** olderPsDerivationSameUniqueId.derivation_contents の中から、同じ位置のものを取得。 */
  //                   const olderPsDerivationContent = olderPsDerivationSameUniqueId.derivation_contents[psDerivationContentIndex];

  //                   if (olderPsDerivationContent) {
  //                     // 中身を比較。
  //                     if (
  //                       (olderPsDerivationContent.represent === psDerivationContent.represent)
  //                       &&
  //                       (olderPsDerivationContent.unnecessary === psDerivationContent.unnecessary)
  //                       &&
  //                       // <img src="http://localhost:8000/api/pss/illust/1?expires=1684846855&signature=a976dde2015021658f8db26a674771ed5a2737a7c8969640146be509abbc9f86" /> の、「?expires=」より後ろは毎回異なるので、「?」以降を除いて比較する。
  //                       (
  //                         getImgSrcParametersRemovedXml(olderPsDerivationContent.content_xml as string)
  //                         ===
  //                         getImgSrcParametersRemovedXml(psDerivationContent.content_xml as string)
  //                       )
  //                     ) {
  //                       // 変更が無い。
  //                       // 'Added' で登録していたのを 'NoChange' で上書き。
  //                       psDerivationContentIdDiffTypeMap.set(
  //                         psDerivationContent.derivation_content_id as number,
  //                         'NoChange'
  //                       );
  //                     }
  //                     else {
  //                       // 変更があった。
  //                       // 'Added' で登録していたのを 'Modified' で上書き。
  //                       psDerivationContentIdDiffTypeMap.set(
  //                         psDerivationContent.derivation_content_id as number,
  //                         'Modified'
  //                       );
  //                     }
  //                   }
  //                 }
  //               }
  //             }
  //           );
  //         }

  //         // TODO: 削除された derivation_judgements を追加。テキスト確定条件と合致するか、位置を確認。
  //         // TODO: unique_id で同じテキスト確定条件か判別する必要アリ。
  //         // TODO: put ps の時に、ps_text_condition_id をちゃんと設定。

  //         // derivation_judgements を比較。
  //         if (psDerivation.derivation_judgements) {
  //           psDerivation.derivation_judgements.forEach(
  //             (psDerivationJudgement: PsDerivationJudgement) => {
  //               // とりあえず、追加されたものとしておく。
  //               // 以下の場合に対応:
  //               // ・olderPs.derivations に無い PsDerivation が ps.derivations にある → derivation_judgement も追加されたものとする。
  //               // ・olderPsDerivationSameUniqueId.derivation_judgements が配列じゃない (可能性は無いはず)。
  //               // TODO: unique_id
  //               // ・olderPsDerivationSameUniqueId.derivation_judgements の中から、同じ unique_id を持つ PsTextCondition に対応するものが見つからない。
  //               pdjiDtMap.set(
  //                 psDerivationJudgement.derivation_judgement_id as number,
  //                 'Added'
  //               );

  //               if (olderPsDerivationSameUniqueId) {
  //                 // 同じ unique_id を持つものが見つかった。

  //                 if (olderPsDerivationSameUniqueId.derivation_judgements) {
  //                   /** olderPs.derivations[].derivation_judgements の中から、同じ derivation_judgement_id を持つものを取得。 */
  //                   const olderPsDerivationJudgementSameId = olderPsDerivationSameUniqueId.derivation_judgements.find(
  //                     (olderPsDerivationJudgement: PsDerivationJudgement) =>
  //                       (olderPsDerivationJudgement.derivation_judgement_id === psDerivationJudgement.derivation_judgement_id)
  //                   );

  //                   if (olderPsDerivationJudgementSameId) {
  //                     // 中身を比較。
  //                     if (
  //                       (olderPsDerivationJudgementSameId.ps_text_condition_id === psDerivationJudgement.ps_text_condition_id)
  //                       &&
  //                       (olderPsDerivationJudgementSameId.value === psDerivationJudgement.value)
  //                     ) {
  //                       // 変更が無い。
  //                       // 'Added' で登録していたのを 'NoChange' で上書き。
  //                       pdjiDtMap.set(
  //                         psDerivationJudgement.derivation_judgement_id as number,
  //                         'NoChange'
  //                       );
  //                     }
  //                     else {
  //                       // 変更があった。
  //                       // 'Added' で登録していたのを 'Modified' で上書き。
  //                       pdjiDtMap.set(
  //                         psDerivationJudgement.derivation_judgement_id as number,
  //                         'Modified'
  //                       );
  //                     }
  //                   }
  //                 }
  //               }
  //             }
  //           );
  //         }
  //       }
  //     );

  //     // 削除されていた派生を、新バージョンの派生の配列とマージ。
  //     olderPs.derivations.forEach(
  //       (olderPsDerivation: PsDerivation) => {
  //         // ps.derivations の中から、olderPsDerivation と同じ unique_id を持つものを探す。
  //         if ((ps.derivations as PsDerivation[]).every(
  //           (psDerivation: PsDerivation) =>
  //             (psDerivation.unique_id !== olderPsDerivation.unique_id)
  //         )) {
  //           // 同じ unique_id を持つものが ps.derivations に見つからなかった場合。

  //           // 削除されていた派生を新バージョンの派生の配列に追加。
  //           mergedPsDerivations.push(olderPsDerivation);

  //           if (olderPsDerivation.derivation_contents) {
  //             olderPsDerivation.derivation_contents.forEach(
  //               (olderPsDerivationContent: PsDerivationContent) => {
  //                 // PsDerivationContent も削除されたものとして登録。
  //                 psDerivationContentIdDiffTypeMap.set(
  //                   olderPsDerivationContent.derivation_content_id as number,
  //                   'Deleted'
  //                 );
  //               }
  //             );
  //           }

  //           if (olderPsDerivation.derivation_judgements) {
  //             olderPsDerivation.derivation_judgements.forEach(
  //               (olderPsDerivationJudgement: PsDerivationJudgement) => {
  //                 // PsDerivationJudgement も削除されたものとして登録。
  //                 psDerivationJudgementIdDiffTypeMap.set(
  //                   olderPsDerivationJudgement.derivation_judgement_id as number,
  //                   'Deleted'
  //                 );
  //               }
  //             );
  //           }
  //         }
  //       }
  //     );
  //   }
  // }

  // このコンポーネントおよび配下のコンポーネントでは、React Hook Form を使用し、描画し、setValue() 等する構造となっている。
  // React Hook Form の append() や update() するためには useEffect() の使用が必要か。
  useEffect(() => {
    // olderPs は PS 比較画面の時のみ prop で渡される。
    if (!isDiffedRef.current && mode === 'Diff' && diffMode === 'Diff' && olderPs) {
      // 一度限り実行。
      isDiffedRef.current = true;

      /** appendTextCondition() は即座に反映されないので、derivation_judgements の比較用に使用。 */
      const mergedPsTextConditions: PsTextCondition[] = Array.isArray(textConditions) ? [...textConditions] : [];

      // テキスト確定条件を比較。
      if (ps.text_conditions && olderPs.text_conditions) {
        ps.text_conditions.forEach((psTextCondition: PsTextCondition) => {
          // とりあえず追加されたものとして Map に登録しておく。
          psTextConditionIdDiffTypeMapRef.current.set(psTextCondition.text_condition_id as number, 'Added');

          /** olderPs.text_conditions の中から、同じ unique_id を持つものを取得。 */
          const olderPsTextConditionSameUniqueId = (olderPs.text_conditions as PsTextCondition[]).find(
            (olderPsTextCondition: PsTextCondition) => olderPsTextCondition.unique_id === psTextCondition.unique_id
          );

          if (olderPsTextConditionSameUniqueId) {
            // 同じ unique_id を持つものが見つかったら内容を比較。
            if (
              psTextCondition.text_condition === olderPsTextConditionSameUniqueId.text_condition &&
              // text_condition_drawings を比較。
              comparePsTextConditionDrawings(
                olderPsTextConditionSameUniqueId.text_condition_drawings,
                psTextCondition.text_condition_drawings
              ) &&
              // text_condition_flists を比較。
              comparePsTextConditionFlists(
                olderPsTextConditionSameUniqueId.text_condition_flists,
                psTextCondition.text_condition_flists
              ) &&
              psTextCondition.service_spec_table === olderPsTextConditionSameUniqueId.service_spec_table &&
              psTextCondition.tos === olderPsTextConditionSameUniqueId.tos &&
              psTextCondition.tos_supplement === olderPsTextConditionSameUniqueId.tos_supplement &&
              psTextCondition.hg_check_require === olderPsTextConditionSameUniqueId.hg_check_require
            ) {
              // 変更が無い。
              // 'Added' で登録していたのを 'NoChange' で上書き。
              psTextConditionIdDiffTypeMapRef.current.set(psTextCondition.text_condition_id as number, 'NoChange');
            } else {
              // 変更があった。
              // 'Added' で登録していたのを 'Modified' で上書き。
              psTextConditionIdDiffTypeMapRef.current.set(psTextCondition.text_condition_id as number, 'Modified');
            }
          }
        });

        // 削除されていたテキスト確定条件を、新バージョンのテキスト確定条件の配列とマージ (form value に追加)。
        olderPs.text_conditions.forEach((olderPsTextCondition: PsTextCondition) => {
          // ps.text_conditions の中から、olderPsTextCondition と同じ unique_id を持つものを探す。
          if (
            (ps.text_conditions as PsTextCondition[]).every(
              (psTextCondition: PsTextCondition) => psTextCondition.unique_id !== olderPsTextCondition.unique_id
            )
          ) {
            // 同じ unique_id を持つものが ps.text_conditions に見つからなかった場合。

            // 削除されていたテキスト確定条件を新バージョンのテキスト確定条件の配列に追加 (form value に追加)。
            appendTextCondition(olderPsTextCondition);
            // derivation_judgements の比較用。
            mergedPsTextConditions.push(olderPsTextCondition);

            // 削除されていたものとして Map に登録。
            psTextConditionIdDiffTypeMapRef.current.set(olderPsTextCondition.text_condition_id as number, 'Deleted');
          }
        });
      }

      // console.log('mergedPsTextConditions:', mergedPsTextConditions);

      // 派生を比較。
      if (ps.derivations && olderPs.derivations) {
        ps.derivations.forEach((psDerivation: PsDerivation, psDerivationIndex: number) => {
          // derivations を比較。

          /** olderPs.derivations の中から、同じ unique_id を持つものを取得。 */
          const olderPsDerivationSameUniqueId = (olderPs.derivations as PsDerivation[]).find(
            (olderPsDerivation: PsDerivation) => olderPsDerivation.unique_id === psDerivation.unique_id
          );

          // derivation_contents を比較。
          if (psDerivation.derivation_contents) {
            psDerivation.derivation_contents.forEach(
              (psDerivationContent: PsDerivationContent, psDerivationContentIndex: number) => {
                // とりあえず、追加されたものとしておく。
                // 以下の場合に対応:
                // ・olderPs.derivations に無い PsDerivation が ps.derivations にある → derivation_content も追加されたものとする。
                // ・olderPsDerivationSameUniqueId.derivation_contents が配列じゃない (可能性は無いはず)。
                // ・olderPsDerivationSameUniqueId.derivation_contents の中から、同じ位置のものが見つからない (可能性は無いはず)。
                psDerivationContentIdDiffTypeMapRef.current.set(
                  psDerivationContent.derivation_content_id as number,
                  'Added'
                );

                if (olderPsDerivationSameUniqueId) {
                  // 同じ unique_id を持つものが見つかった。

                  if (olderPsDerivationSameUniqueId.derivation_contents) {
                    /** olderPsDerivationSameUniqueId.derivation_contents の中から、同じ位置のものを取得。 */
                    const olderPsDerivationContent =
                      olderPsDerivationSameUniqueId.derivation_contents[psDerivationContentIndex];

                    if (olderPsDerivationContent) {
                      // 中身を比較。
                      if (
                        olderPsDerivationContent.represent === psDerivationContent.represent &&
                        olderPsDerivationContent.unnecessary === psDerivationContent.unnecessary &&
                        // <img src="http://localhost:8000/api/pss/illust/1?expires=1684846855&signature=a976dde2015021658f8db26a674771ed5a2737a7c8969640146be509abbc9f86" /> の、「?expires=」より後ろは毎回異なるので、「?」以降を除いて比較する。
                        getImgSrcParametersRemovedXml(olderPsDerivationContent.content_xml as string) ===
                          getImgSrcParametersRemovedXml(psDerivationContent.content_xml as string)
                      ) {
                        // 変更が無い。
                        // 'Added' で登録していたのを 'NoChange' で上書き。
                        psDerivationContentIdDiffTypeMapRef.current.set(
                          psDerivationContent.derivation_content_id as number,
                          'NoChange'
                        );
                      } else {
                        // 変更があった。
                        // 'Added' で登録していたのを 'Modified' で上書き。
                        psDerivationContentIdDiffTypeMapRef.current.set(
                          psDerivationContent.derivation_content_id as number,
                          'Modified'
                        );
                      }

                      // 対応する変更前xmlをセット
                      psDerivationContentIdDiffXmlMapRef.current.set(
                        psDerivationContent.derivation_content_id as number,
                        olderPsDerivationContent.content_xml_edit ?? ''
                      );
                    }
                  }
                }
              }
            );
          }

          // derivation_judgements を比較。
          // if (psDerivation.derivation_judgements) {
          //   psDerivation.derivation_judgements.forEach(
          //     (psDerivationJudgement: PsDerivationJudgement) => {
          //       // とりあえず、追加されたものとしておく。
          //       // 以下の場合に対応:
          //       // ・olderPs.derivations に無い PsDerivation が ps.derivations にある → derivation_judgement も追加されたものとする。
          //       // ・olderPsDerivationSameUniqueId.derivation_judgements の中から、unique_id が同じ PsTextCondition が見つからない (追加された PsTextCondition に対する PsDerivationJudgement ということ)。
          //       // ・olderPsDerivationSameUniqueId.derivation_judgements が配列じゃない (可能性は無いはず) 場合や、その他比較対象が見つからない場合。
          //       psDerivationJudgementIdDiffTypeMapRef.current.set(
          //         psDerivationJudgement.derivation_judgement_id as number,
          //         'Added'
          //       );

          //       if (olderPsDerivationSameUniqueId) {
          //         // 旧バージョンの派生の中から、同じ unique_id を持つものが見つかった。

          //         if (olderPsDerivationSameUniqueId.derivation_judgements) {

          //           /** psDerivationJudgement の ps_text_condition_id と合致する PsTextCondition を取得。 */
          //           const newerPsTextCondition = (Array.isArray(ps.text_conditions)) ?
          //             ps.text_conditions.find(
          //               (psTextCondition: PsTextCondition) =>
          //                 psTextCondition.text_condition_id === psDerivationJudgement.ps_text_condition_id
          //             )
          //             :
          //             undefined;

          //           if (newerPsTextCondition) {

          //             /** psTextCondition.unique_id と合致する旧バージョンの PsTextCondition を取得。 */
          //             const olderPsTextConditionSameUniqueId = (Array.isArray(olderPs.text_conditions)) ?
          //               olderPs.text_conditions.find(
          //                 (olderPsTextCondition: PsTextCondition) =>
          //                   olderPsTextCondition.unique_id === newerPsTextCondition.unique_id
          //               )
          //               :
          //               undefined;

          //             if (olderPsTextConditionSameUniqueId) {

          //               /** olderPsTextConditionSameUniqueId.text_condition_id に合致する旧バージョンの PsDerivationJudgement を取得。 */
          //               const olderPsDerivationJudgement = olderPsDerivationSameUniqueId.derivation_judgements.find(
          //                 (olderPDJ: PsDerivationJudgement) =>
          //                   olderPDJ.ps_text_condition_id === olderPsTextConditionSameUniqueId.text_condition_id
          //               );

          //               if (olderPsDerivationJudgement) {
          //                 // 内容を比較。

          //                 psDerivationJudgementIdDiffTypeMapRef.current.set(
          //                   psDerivationJudgement.derivation_judgement_id as number,
          //                   // unique_id が同じで value も同じかどうか。
          //                   (olderPsDerivationJudgement.value === psDerivationJudgement.value) ?
          //                     'NoChange'
          //                     :
          //                     'Modified'
          //                 );
          //               }
          //             }
          //           }
          //         }
          //       }
          //     }
          //   );
          // }

          /** 削除されたテキスト確定条件に対する PS 派生判定の追加も必要。新しい derivation_judgements にする。 */
          const newDerivationJudgements: PsDerivationJudgement[] = [];

          // テキスト確定条件ごとに判断。mergedPsTextConditions は新バージョンの後に旧バージョンの PsTextConditions が入っている。
          mergedPsTextConditions.forEach((psTextCondition: PsTextCondition) => {
            // ↑ psTextCondition は新バージョンかもしれないし、旧バージョンかもしれない。

            /** psTextCondition と同じ unique_id を持つ旧バージョンの PsTextCondition を取得。 */
            const olderPsTextConditionSameUniqueId = Array.isArray(olderPs.text_conditions)
              ? olderPs.text_conditions.find(
                  (olderPsTextCondition: PsTextCondition) =>
                    olderPsTextCondition.unique_id === psTextCondition.unique_id
                )
              : undefined;

            /** psTextCondition と同じ unique_id を持つ新バージョンの PsTextCondition を取得。 */
            const newerPsTextConditionSameUniqueId = Array.isArray(ps.text_conditions)
              ? ps.text_conditions.find(
                  (newerPsTextCondition: PsTextCondition) =>
                    newerPsTextCondition.unique_id === psTextCondition.unique_id
                )
              : undefined;

            /** 旧バージョンの PsDerivationJudgement を取得。unique_id → text_condition_id で判断。 */
            const olderPsDerivationJudgement =
              olderPsDerivationSameUniqueId &&
              Array.isArray(olderPsDerivationSameUniqueId.derivation_judgements) &&
              olderPsTextConditionSameUniqueId
                ? olderPsDerivationSameUniqueId.derivation_judgements.find(
                    (psDerivationJudgement: PsDerivationJudgement) =>
                      psDerivationJudgement.ps_text_condition_id === olderPsTextConditionSameUniqueId.text_condition_id
                  )
                : undefined;

            /** 新バージョンの PsDerivationJudgement を取得。unique_id → text_condition_id で判断。 */
            const newerPsDerivationJudgement =
              Array.isArray(psDerivation.derivation_judgements) && newerPsTextConditionSameUniqueId
                ? psDerivation.derivation_judgements.find(
                    (psDerivationJudgement: PsDerivationJudgement) =>
                      psDerivationJudgement.ps_text_condition_id === newerPsTextConditionSameUniqueId.text_condition_id
                  )
                : undefined;

            if (olderPsDerivationJudgement && newerPsDerivationJudgement) {
              // 両方あったら内容を比較。

              psDerivationJudgementIdDiffTypeMapRef.current.set(
                newerPsDerivationJudgement.derivation_judgement_id as number,
                // unique_id が同じで value も同じかどうか。
                olderPsDerivationJudgement.value === newerPsDerivationJudgement.value ? 'NoChange' : 'Modified'
              );

              // 新バージョンのps_derivation_judgement_idに対応する旧バージョンの値
              psDerivationJudgementIdDiffValueMapRef.current.set(
                newerPsDerivationJudgement.derivation_judgement_id as number,
                olderPsDerivationJudgement.value ?? 0
              );

              // 新バージョンを新しい derivation_judgements として追加。
              newDerivationJudgements.push(newerPsDerivationJudgement);
            } else {
              if (olderPsDerivationJudgement) {
                // 旧にあるが新にない。
                // 削除されたものとして Map に登録。
                psDerivationJudgementIdDiffTypeMapRef.current.set(
                  olderPsDerivationJudgement.derivation_judgement_id as number,
                  'Deleted'
                );
                // 新しい derivation_judgements として追加。
                newDerivationJudgements.push(olderPsDerivationJudgement);

                // 新バージョンのps_derivation_judgement_idに対応する旧バージョンの値
                // 新は削除されているので、idとして旧のps_derivation_judgement_idをセット
                psDerivationJudgementIdDiffValueMapRef.current.set(
                  olderPsDerivationJudgement.derivation_judgement_id as number,
                  olderPsDerivationJudgement.value ?? 0
                );
              }
              if (newerPsDerivationJudgement) {
                // 旧にないが新にある。
                // 追加されたものとして Map に登録。
                psDerivationJudgementIdDiffTypeMapRef.current.set(
                  newerPsDerivationJudgement.derivation_judgement_id as number,
                  'Added'
                );
                // 新しい derivation_judgements として追加。
                newDerivationJudgements.push(newerPsDerivationJudgement);

                // 新バージョンのps_derivation_judgement_idに対応する旧バージョンの値
                // 追加なので、値は0
                psDerivationJudgementIdDiffValueMapRef.current.set(
                  newerPsDerivationJudgement.derivation_judgement_id as number,
                  0
                );
              }
            }
          });

          // console.log('newDerivationJudgements:', newDerivationJudgements);

          // JSON の stringify と parse でもいいが。
          // 新しい PsDerivation を作成。
          const newPsDerivation = _.cloneDeep(psDerivation);

          // derivation_judgements を置き換え。
          newPsDerivation.derivation_judgements = newDerivationJudgements;

          // console.log('newPsDerivation: ', newPsDerivation);

          // 派生を更新。
          updateDerivation(psDerivationIndex, newPsDerivation);
        });

        // 削除されていた派生を、新バージョンの派生の配列とマージ (form value に追加)。
        olderPs.derivations.forEach((olderPsDerivation: PsDerivation) => {
          // ps.derivations の中から、olderPsDerivation と同じ unique_id を持つものを探す。
          if (
            (ps.derivations as PsDerivation[]).every(
              (psDerivation: PsDerivation) => psDerivation.unique_id !== olderPsDerivation.unique_id
            )
          ) {
            // 同じ unique_id を持つものが ps.derivations に見つからなかった場合。

            // 削除されていた派生を新バージョンの派生の配列に追加 (form value に追加)。
            appendDerivation(olderPsDerivation);

            if (olderPsDerivation.derivation_contents) {
              olderPsDerivation.derivation_contents.forEach((olderPsDerivationContent: PsDerivationContent) => {
                // PsDerivationContent も削除されたものとして登録。
                psDerivationContentIdDiffTypeMapRef.current.set(
                  olderPsDerivationContent.derivation_content_id as number,
                  'Deleted'
                );

                psDerivationContentIdDiffXmlMapRef.current.set(
                  olderPsDerivationContent.derivation_content_id as number,
                  olderPsDerivationContent.content_xml_edit ?? ''
                );
              });
            }

            if (olderPsDerivation.derivation_judgements) {
              olderPsDerivation.derivation_judgements.forEach((olderPsDerivationJudgement: PsDerivationJudgement) => {
                // PsDerivationJudgement も削除されたものとして登録。
                psDerivationJudgementIdDiffTypeMapRef.current.set(
                  olderPsDerivationJudgement.derivation_judgement_id as number,
                  'Deleted'
                );
              });
            }
          }
        });
      }

      // タッチアップ項目の配列を比較。
      if (ps.touchups && olderPs.touchups) {
        ps.touchups.forEach((psTouchup: PsTouchup) => {
          // touchups を比較。

          /** olderPs.touchups の中から、同じ unique_id を持つものを取得。 */
          const olderPsTouchupSameUniqueId = (olderPs.touchups as PsTouchup[]).find(
            (olderPsTouchup: PsTouchup) => olderPsTouchup.unique_id === psTouchup.unique_id
          );

          // touch_up と judgement_information を比較。

          // とりあえず、追加されたものとしておく。
          psTouchupIdTouchUpDiffTypeMapRef.current.set(psTouchup.touchup_id as number, 'Added');
          psTouchupIdJudgementInformationDiffTypeMapRef.current.set(psTouchup.touchup_id as number, 'Added');

          if (olderPsTouchupSameUniqueId) {
            // 同じ unique_id を持つものが見つかった。

            // 中身を比較。
            psTouchupIdTouchUpDiffTypeMapRef.current.set(
              psTouchup.touchup_id as number,
              olderPsTouchupSameUniqueId.touch_up === psTouchup.touch_up ? 'NoChange' : 'Modified'
            );
            psTouchupIdJudgementInformationDiffTypeMapRef.current.set(
              psTouchup.touchup_id as number,
              olderPsTouchupSameUniqueId.judgement_information === psTouchup.judgement_information
                ? 'NoChange'
                : 'Modified'
            );
          }

          // PsTouchupTarget の target と touchup_candidates を比較。
          if (psTouchup.touchup_targets) {
            psTouchup.touchup_targets.forEach((psTouchupTarget: PsTouchupTarget, psTouchupTargetIndex: number) => {
              // とりあえず、追加されたものとしておく。
              psTouchupTargetIdTargetDiffTypeMapRef.current.set(psTouchupTarget.touchup_target_id as number, 'Added');
              psTouchupTargetIdTouchupCandidatesDiffTypeMapRef.current.set(
                psTouchupTarget.touchup_target_id as number,
                'Added'
              );

              if (olderPsTouchupSameUniqueId) {
                // 同じ unique_id を持つものが見つかった。

                if (olderPsTouchupSameUniqueId.touchup_targets) {
                  /** olderPsTouchupSameUniqueId.touchup_targets の中から、同じ位置のものを取得。 */
                  const olderPsTouchupTarget = olderPsTouchupSameUniqueId.touchup_targets[psTouchupTargetIndex];

                  if (olderPsTouchupTarget) {
                    // 中身を比較。
                    psTouchupTargetIdTargetDiffTypeMapRef.current.set(
                      psTouchupTarget.touchup_target_id as number,
                      olderPsTouchupTarget.target === psTouchupTarget.target ? 'NoChange' : 'Modified'
                    );
                    // touchup_candidates は表示用 string に変換して比較。
                    psTouchupTargetIdTouchupCandidatesDiffTypeMapRef.current.set(
                      psTouchupTarget.touchup_target_id as number,
                      touchupCandidatesToString(olderPsTouchupTarget.touchup_candidates) ===
                        touchupCandidatesToString(psTouchupTarget.touchup_candidates)
                        ? 'NoChange'
                        : 'Modified'
                    );
                  }
                }
              }
            });
          }
        });

        // 削除されていた PS タッチアップ項目を、新バージョンの PS タッチアップ項目の配列とマージ (form value に追加)。
        olderPs.touchups.forEach((olderPsTouchup: PsTouchup) => {
          // ps.touchups の中から、olderPsTouchup と同じ unique_id を持つものを探す。
          if ((ps.touchups as PsTouchup[]).every((pT: PsTouchup) => pT.unique_id !== olderPsTouchup.unique_id)) {
            // 同じ unique_id を持つものが ps.touchups に見つからなかった場合。

            // 削除されていた PS タッチアップ項目を新バージョンの PS タッチアップ項目の配列に追加 (form value に追加)。
            appendTouchup(olderPsTouchup);

            // touch_up も削除されたものとして Map に登録。
            psTouchupIdTouchUpDiffTypeMapRef.current.set(olderPsTouchup.touchup_id as number, 'Deleted');

            // judgement_information も削除されたものとして Map に登録。
            psTouchupIdJudgementInformationDiffTypeMapRef.current.set(olderPsTouchup.touchup_id as number, 'Deleted');

            if (olderPsTouchup.touchup_targets) {
              olderPsTouchup.touchup_targets.forEach((olderPsTouchupTarget: PsTouchupTarget) => {
                // target も削除されたものとして Map に登録。
                psTouchupTargetIdTargetDiffTypeMapRef.current.set(
                  olderPsTouchupTarget.touchup_target_id as number,
                  'Deleted'
                );

                // touchup_candidates も削除されたものとして Map に登録。
                psTouchupTargetIdTouchupCandidatesDiffTypeMapRef.current.set(
                  olderPsTouchupTarget.touchup_target_id as number,
                  'Deleted'
                );
              });
            }
          }
        });
      }
    } else if (!isDiffedRef.current && mode === 'Diff' && diffMode === 'Add') {
      // 比較モードの追加の場合、全ての項目に「追加」フラグを立てる

      // 一度限り実行。
      isDiffedRef.current = true;

      // テキスト確定条件
      if (ps.text_conditions) {
        ps.text_conditions.forEach((psTextCondition: PsTextCondition) => {
          psTextConditionIdDiffTypeMapRef.current.set(psTextCondition.text_condition_id as number, 'Added');
        });
      }
      // 派生を比較。
      if (ps.derivations) {
        ps.derivations.forEach((psDerivation: PsDerivation) => {
          if (psDerivation.derivation_contents) {
            psDerivation.derivation_contents.forEach((psDerivationContent: PsDerivationContent) => {
              psDerivationContentIdDiffTypeMapRef.current.set(
                psDerivationContent.derivation_content_id as number,
                'Added'
              );
            });
          }
          if (psDerivation.derivation_judgements) {
            psDerivation.derivation_judgements.forEach((psDerivationJudgement: PsDerivationJudgement) => {
              psDerivationJudgementIdDiffTypeMapRef.current.set(
                psDerivationJudgement.derivation_judgement_id as number,
                'Added'
              );
            });
          }
        });
      }
      // タッチアップ項目の配列を比較。
      if (ps.touchups) {
        ps.touchups.forEach((psTouchup: PsTouchup) => {
          psTouchupIdTouchUpDiffTypeMapRef.current.set(psTouchup.touchup_id as number, 'Added');
          psTouchupIdJudgementInformationDiffTypeMapRef.current.set(psTouchup.touchup_id as number, 'Added');
          if (psTouchup.touchup_targets) {
            psTouchup.touchup_targets.forEach((psTouchupTarget: PsTouchupTarget) => {
              psTouchupTargetIdTargetDiffTypeMapRef.current.set(psTouchupTarget.touchup_target_id as number, 'Added');
              psTouchupTargetIdTouchupCandidatesDiffTypeMapRef.current.set(
                psTouchupTarget.touchup_target_id as number,
                'Added'
              );
            });
          }
        });
      }
    }
  }, [
    appendDerivation,
    appendTextCondition,
    appendTouchup,
    mode,
    olderPs,
    ps.derivations,
    ps.text_conditions,
    ps.touchups,
    textConditions,
    updateDerivation,
    diffMode,
  ]);

  // end: PS 比較画面関連。

  /** 追加・編集状態。 */
  const isAddingOrEditing = mode === 'Add' || mode === 'Edit';

  if (mode === 'Edit') {
    if (ps.deleted_flag) {
      return <div className="browsefr">{t('廃止済みのPSは編集できません。')}</div>;
    }

    // 基本的にはバージョン番号が最大の PS バージョンが最新 (latest = 1) になるが、その PS に改廃案がある場合は最大のバージョン番号は改廃案 (state = 0) になり、2 番目に大きなバージョン番号が承認済の最新 (latest = 1) となる。

    // 承認済み最新バージョンと改廃案は編集可能、それら以外は不可とする。
    if (!ps.latest && ps.state !== 0) {
      return <div className="browsefr">{t('承認済み最新バージョンと改廃案以外のPSは編集できません。')}</div>;
    }
  }

  return (
    <>
      <ScrollToHash />
      <FormProvider {...useFormReturnProps}>
        <div
          // key={ps.ps_id}
          className="browsefr"
          // style={{
          //   overflow: 'initial',
          // }}
        >
          {isAddingOrEditing && (
            <div className="master-navi mb20">
              <ul>
                <li
                  onClick={handleSubmit(
                    // getValues() では yup の transform() はされないようだ。handleSubmit() を使用すれば transform() されるようだ。
                    (data: unknown) => {
                      // console.log('handleSubmit data: ', data);
                      // console.log(
                      //   'getValues(): ',
                      //   getValues()
                      // );

                      const newData = {
                        ...(data as Ps),

                        // ドラッグ & ドロップで変更された child_tree_items を設定。
                        child_tree_items:
                          mode === 'Edit' && modifiedRootPsTreeItem
                            ? (getPsTreeItem(modifiedRootPsTreeItem, ps.ps_id as number) as PsTreeItem).child_tree_items
                            : (data as Ps).child_tree_items,
                      };

                      // if (modifiedRootPsTreeItem) {
                      //   console.log('modifiedRootPsTreeItem:', modifiedRootPsTreeItem);

                      //   newData.child_tree_items = (getPsTreeItem(
                      //     modifiedRootPsTreeItem,
                      //     ps.ps_version_id as number
                      //   ) as PsTreeItem).child_tree_items;

                      //   // setValue(
                      //   //   `child_tree_items`,
                      //   //   (getPsTreeItem(
                      //   //     modifiedRootPsTreeItem,
                      //   //     ps.ps_version_id as number
                      //   //   ) as PsTreeItem).child_tree_items);
                      // }

                      if (mode === 'Add') {
                        // API 呼び出し。PS 新規作成。
                        postPsAddMutation.mutate({
                          // data: data as Ps,
                          data: newData as Ps,
                        });
                      } else if (mode === 'Edit') {
                        // API 呼び出し。PS 更新。
                        postPsEditMutation.mutate({
                          // psVersionId: Number((data as PsSchemaType).ps_version_id),
                          psVersionId: Number((newData as PsSchemaType).ps_version_id),
                          // data: data as Ps,
                          data: newData as Ps,
                        });
                      }

                      // PS ツリーの更新をしてもいい状態にする。
                      setCanSetDoRePostPsTreeSearch(true);
                    },
                    (errors: unknown) => {
                      // console.log('handleSubmit errors: ', errors);

                      // モーダルでエラー メッセージを表示する。
                      setHandleSubmitErrors(errors);
                    }
                  )}
                >
                  <input value={t('改廃案保存').toString()} className="searchbtn" type="button" />
                </li>
              </ul>
            </div>
          )}

          {/* PS比較 差分箇所 */}
          {mode === 'Diff' && diffMode === 'Diff' && (
            <div className="mb20">
              <h3 className="mdh2 ft15">
                <button
                  type="button"
                  className="text-button"
                  onClick={() => setIsDifferencesInfoAreaOpen(!isDifferencesInfoAreaOpen)}
                >
                  {t('変更箇所')}
                  {!ps.ps_differences && t('なし')}
                  <i className={`fa-solid ${isDifferencesInfoAreaOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
                </button>
              </h3>
              <SlideToggle open={isDifferencesInfoAreaOpen}>
                <div className="ps-diff-info">
                  <ul>
                    {ps.ps_differences?.map((differenceItem) => (
                      <li key={differenceItem.anker_id}>
                        <Link to={`#${differenceItem.anker_id ?? ''}`}>{differenceItem.kbn_name}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </SlideToggle>
            </div>
          )}

          {/* PS 属性情報。 */}
          <PsAttributes
            mode={mode}
            ps={ps}
            olderPs={olderPs}
            // register={register}
          />

          <div className="langfr">
            {/* 表示言語切り替えチェックボックス。 */}
            <div className="langcheck">
              {[t('日本語'), t('米語'), t('英語')].map((language: string, languageIndex: number) => (
                <span
                  key={language}
                  className="ck-left2"
                  onClick={() => {
                    const newDisplayLanguages = Array.from(displayLanguages);
                    newDisplayLanguages.splice(languageIndex, 1, !displayLanguages[languageIndex]);
                    setDisplayLanguages(newDisplayLanguages);
                  }}
                  style={{ marginRight: '0.5rem' }}
                >
                  <input type="checkbox" checked={displayLanguages[languageIndex]} readOnly />
                  <label>{language}</label>
                </span>
              ))}
            </div>
            {/* 表示切替ボタン エリア。 */}
            <div className="alldisplay">
              <ul>
                <li className="opbtn">
                  <span
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      // ・全て開く/表示: PS テキスト表示エリアのアコーディオンをすべて開いて表示。

                      // 「派生文書のみ表示」をオフに。
                      setDoShowDerivationsOnly(false);
                      setIsJudgementInfoAreaOpen(true);
                      setIsTouchupInfoAreaOpen(true);
                      setIsManagementInfoAreaOpen(true);
                      setIsLegalInfoAreaOpen(true);
                      setIsOtherMediaInfoAreaOpen(true);
                      setIsVersionsInfoAreaOpen(true);
                    }}
                  >
                    <i className="fa-solid fa-book-open" style={{ cursor: 'pointer' }} />
                    {t('全て開く/表示')}
                  </span>
                  <button
                    type="button"
                    className="text-button"
                    onClick={() => {
                      setIsSwitchDisplayButtonsAreaOpen(!isSwitchDisplayButtonsAreaOpen);
                    }}
                  >
                    <i
                      className={`fa-solid ${
                        isSwitchDisplayButtonsAreaOpen ? 'fa-chevron-up' : 'fa-chevron-down'
                      } udarrow`}
                    />
                  </button>
                  <SlideToggle open={isSwitchDisplayButtonsAreaOpen}>
                    <ul style={{ display: 'block' }}>
                      <li
                        onClick={() => {
                          // ・全て閉じる: PS テキスト表示エリアのアコーディオンをすべて閉じて表示。

                          // 「派生文書のみ表示」をオフに。
                          setDoShowDerivationsOnly(false);

                          setIsJudgementInfoAreaOpen(false);
                          setIsTouchupInfoAreaOpen(false);
                          setIsManagementInfoAreaOpen(false);
                          setIsLegalInfoAreaOpen(false);
                          setIsOtherMediaInfoAreaOpen(false);
                          setIsVersionsInfoAreaOpen(false);
                        }}
                        style={{
                          cursor: 'pointer',
                          position: 'relative',
                          zIndex: '1',
                        }}
                      >
                        <i className="fa-solid fa-book" />
                        {t('全て閉じる')}
                      </li>
                      <li
                        onClick={() => {
                          // ・派生文書のみ表示: 派生文書、確定条件、判断情報 (閉) のみ表示し、他を非表示にする。
                          setDoShowDerivationsOnly(true);
                          setIsJudgementInfoAreaOpen(false);
                        }}
                        style={{
                          cursor: 'pointer',
                          position: 'relative',
                          zIndex: '1',
                        }}
                      >
                        <i className="fa-solid fa-code-branch" />
                        {t('派生文書のみ表示')}
                      </li>
                    </ul>
                  </SlideToggle>
                </li>
              </ul>
            </div>
          </div>

          {/* 「制作で押さえるポイント」を「日本語、米語、英語」のチェックボックスの下段に表示する。5 章や 7 章では最も重要な情報が記載されている事があるため。 */}
          <table className="table32 mb10">
            <tbody>
              <tr>
                <th>{t('制作で押さえるポイント')}</th>
                <td
                  onClick={() => {
                    if (mode === 'Diff' && olderPs?.description_key_point !== ps.description_key_point) {
                      setDiffTitle('制作で押さえるポイント');
                      setLanguageId(-1);
                      setPsValue(ps.description_key_point);
                      setPsOldValue(olderPs?.description_key_point);
                      setIsDiffDlgOpen(true);
                    }
                  }}
                  style={{
                    // 比較。
                    backgroundColor:
                      mode === 'Diff' && olderPs?.description_key_point !== ps.description_key_point
                        ? 'Yellow'
                        : 'inherit',
                  }}
                >
                  {isAddingOrEditing ? (
                    <TextInput name={'description_key_point' as never} register={register} errors={formState.errors} />
                  ) : (
                    ps.description_key_point
                  )}
                </td>
              </tr>
            </tbody>
          </table>

          <div className="langtable scroll">
            <table className="table29b" id="anker-content">
              <tbody>
                <tr>
                  {psInfoTabMode && <th />}
                  <th
                    style={{
                      width: '3rem',
                    }}
                  >
                    {t('派生')}
                  </th>
                  {
                    // 表示言語切替 (日本語、米語、英語) チェックボックスの状態に応じ、言語の表示 (チェック時) / 非表示 (非チェック時) を切り替える。
                    [
                      [t('日例'), t('日本語')],
                      [t('米例'), t('米語')],
                      [t('英例'), t('英語')],
                    ].map(
                      (languagePair: string[], languagePairIndex: number) =>
                        displayLanguages[languagePairIndex] && (
                          <React.Fragment key={languagePair[0]}>
                            {isAddingOrEditing && <th className="derivationContentsRepresent">{languagePair[0]}</th>}
                            <th className="derivationContentXml">
                              {ps.type === 1 || (ps.type === 2 && ps.level !== 5) ? (
                                <ul>
                                  <li>{languagePair[1]}</li>
                                  <li>{t('抜粋')}</li>
                                  <li>
                                    {isAddingOrEditing ? (
                                      <span className="valuefr ddfr">
                                        <SingleSelect
                                          name={
                                            (languagePairIndex === 0
                                              ? 'excerpt_status_id_ja'
                                              : languagePairIndex === 1
                                              ? 'excerpt_status_id_us'
                                              : 'excerpt_status_id_en') as never
                                          }
                                          allowEmpty={false}
                                          options={
                                            (languagePairIndex === 0 && ps.excerpt_status_id_ja === 0) ||
                                            (languagePairIndex === 1 && ps.excerpt_status_id_us === 0) ||
                                            (languagePairIndex === 2 && ps.excerpt_status_id_en === 0)
                                              ? [
                                                  `0:${toString(t('未指定'))}`,
                                                  `1:${toString(t('非対象'))}`,
                                                  `2:${toString(t('対象'))}`,
                                                ]
                                              : [`1:${toString(t('非対象'))}`, `2:${toString(t('対象'))}`]
                                          }
                                          register={register}
                                          getValue={getValue}
                                          getLabel={getLabel}
                                          additionalClassName="ddfr"
                                        />
                                      </span>
                                    ) : (
                                      <span
                                        className="valuefr"
                                        style={{
                                          backgroundColor:
                                            mode === 'Diff' &&
                                            ((languagePairIndex === 0 &&
                                              olderPs?.excerpt_status_id_ja !== ps.excerpt_status_id_ja) ||
                                              (languagePairIndex === 1 &&
                                                olderPs?.excerpt_status_id_us !== ps.excerpt_status_id_us) ||
                                              (languagePairIndex === 2 &&
                                                olderPs?.excerpt_status_id_en !== ps.excerpt_status_id_en))
                                              ? 'Yellow'
                                              : 'inherit',
                                          color:
                                            mode === 'Diff' &&
                                            diffMode === 'Diff' &&
                                            ((languagePairIndex === 0 && ps.excerpt_status_id_ja === 0) ||
                                              (languagePairIndex === 1 && ps.excerpt_status_id_us === 0) ||
                                              (languagePairIndex === 2 && ps.excerpt_status_id_en === 0))
                                              ? 'Red'
                                              : 'inherit',
                                        }}
                                      >
                                        {getExcerptStatusText(
                                          languagePairIndex === 0
                                            ? ps.excerpt_status_id_ja
                                            : languagePairIndex === 1
                                            ? ps.excerpt_status_id_us
                                            : ps.excerpt_status_id_en
                                        )}
                                      </span>
                                    )}
                                  </li>
                                </ul>
                              ) : (
                                languagePair[1]
                              )}
                            </th>
                          </React.Fragment>
                        )
                    )
                  }
                  {Number(ps.type) > 1 && (
                    // ルート PS と章 (L1) PS の場合は非表示 (登録不可)。
                    <th colSpan={2} className="textConditionHeader">
                      {t('テキスト確定条件')}
                    </th>
                  )}
                  {
                    // PS バージョンに紐付く全てのテキスト確定条件を表示。
                    // ルート PS と章 (L1) PS の場合は非表示 (登録不可)。
                    Number(ps.type) > 1 &&
                      Array.isArray(textConditions) &&
                      textConditions.map((psTextCondition: PsTextCondition) => (
                        <th key={psTextCondition.text_condition_id}>{psTextCondition.text_condition}</th>
                      ))
                  }
                </tr>
                {/* CKEditor を表示するダイアログは複数表示できるので、順序を管理するために Context を使用する。 */}
                <DraggableModelessDialogsProvider>
                  {derivationFields.map((derivation: Record<'id', string>, derivationIndex: number) => (
                    // TODO: 列数が変わった時の幅調整。
                    <tr key={derivation.id}>
                      {
                        // PS 情報タブ モードの時。
                        psInfoTabMode && (
                          <td>
                            <input
                              type="radio"
                              name="selectDerivationRadioButtons"
                              value={String(derivations[derivationIndex].derivation_id)}
                              checked={
                                psInfoTabSelectId !== undefined &&
                                String(derivations[derivationIndex].derivation_id) === psInfoTabSelectId
                              }
                              onChange={(event) => {
                                if (typeof setSelectedDerivationId === 'function') {
                                  // ラジオ ボタンで派生を選択時に関数を呼び出す。引数に選択された派生の ID を渡す。
                                  if (event.target.value !== '') {
                                    setPsInfoTabSelectId(event.target.value);
                                    setSelectedDerivationId(derivations[derivationIndex].derivation_id);
                                  }
                                }
                              }}
                            />
                          </td>
                        )
                      }
                      {/* 配列に入っている順番で derivation_no = 1, 2, 3... と付ける。 */}
                      <td>
                        {derivationIndex + 1}
                        {isAddingOrEditing && (
                          <span className="control">
                            <span
                              className="faup"
                              onClick={() => {
                                if (derivationIndex > 0) {
                                  // 派生の順序変更。
                                  // 上と swap。
                                  swapDerivations(derivationIndex, derivationIndex - 1);
                                }
                              }}
                            >
                              <i className="fa-solid fa-sort-up" />
                            </span>
                            <span
                              className="fadown"
                              onClick={() => {
                                if (derivationIndex < derivations.length - 1) {
                                  // 派生の順序変更。
                                  // 下と swap。
                                  swapDerivations(derivationIndex, derivationIndex + 1);
                                }
                              }}
                            >
                              <i className="fa-solid fa-sort-down" />
                            </span>
                            <span
                              onClick={() => {
                                // 派生削除。
                                // 確認ダイアログを表示後、派生を削除する。
                                setPsDerivationIndexToRemove(derivationIndex);
                              }}
                            >
                              <i className="fa-solid fa-circle-xmark" />
                            </span>
                          </span>
                        )}
                      </td>

                      {/* 
                          参考:

                          https://github.com/react-hook-form/react-hook-form/discussions/3702#discussioncomment-1416171

                          https://codesandbox.io/s/react-hook-form-usefieldarray-nested-arrays-m8w6j?file=/src/fieldArray.js 
                        */}
                      <PsContext.Provider value={ps}>
                        <PsDerivationRow
                          mode={mode}
                          psVersionId={ps.ps_version_id as number}
                          psDerivationIndex={derivationIndex}
                          displayLanguages={displayLanguages}
                          psDerivationContentIdDiffTypeMap={psDerivationContentIdDiffTypeMapRef.current}
                          psDerivationJudgementIdDiffTypeMap={psDerivationJudgementIdDiffTypeMapRef.current}
                          psDerivationContentIdDiffXmlMap={psDerivationContentIdDiffXmlMapRef.current}
                          psDerivationJudgementIdDiffValueMapRef={psDerivationJudgementIdDiffValueMapRef.current}
                          psNo={ps.ps_no}
                          psDerivationId={(derivation as PsDerivation)?.derivation_id}
                          fsrId={ps.fsr_id}
                          psType={ps.type}
                          psLevel={ps.level}
                        />
                      </PsContext.Provider>
                    </tr>
                  ))}
                </DraggableModelessDialogsProvider>

                {
                  // ルート PS と章 (L1) PS で閲覧・比較モードの場合は非表示。
                  (Number(ps.type) > 1 || isAddingOrEditing) && (
                    <tr>
                      {/* 追加・編集モードでは派生追加ボタン、それ以外では判断情報より左の空白部分。 */}
                      <td
                        colSpan={
                          // 表示言語切替チェックボックスの状態・PS 情報タブ モード次第で列数が変化するので、ここで計算する。
                          displayLanguages.reduce(
                            (accumulator, currentValue) => accumulator + Number(currentValue),
                            0
                          ) *
                            // 追加・編集モードでは「(言語) 例」列もある。
                            (isAddingOrEditing ? 2 : 1) +
                          (psInfoTabMode ? 2 : 1)
                        }
                        rowSpan={isAddingOrEditing ? 8 : 7}
                        style={{
                          border: 'none',
                          padding: '0',
                        }}
                      >
                        {isAddingOrEditing &&
                          // ルート PS と章 (L1) PS では、派生は最大 1 個まで登録可。
                          // ルート PS と章 (L1) PS 以外か、
                          (Number(ps.type) > 1 ||
                            // ルート PS と章 (L1) PS であって、現時点で派生が 0 個の場合に表示する。
                            derivationFields.length < 1) && (
                            // 派生追加ボタン。
                            <div
                              // className='judpluse'
                              // className='judpluse2'
                              style={{
                                float: 'left',
                                padding: '10px 0 0 16px',
                                // textAlign: 'left',
                              }}
                            >
                              <span
                                className="modal-open"
                                // data-modal='popup13.html'
                                onClick={() => {
                                  // 空の派生を派生一覧の末尾に 1 行追加する。
                                  appendDerivation({
                                    // 適当に被らないであろう ID を振る。
                                    derivation_id: Date.now(),
                                    derivation_contents: [
                                      {
                                        // 適当に被らないであろう ID を振る。
                                        derivation_content_id: Date.now(),
                                        // 0 → 1 個になる時は代表例とする。
                                        represent: derivationFields.length === 0,
                                        unnecessary: false,
                                        content_xml: null,
                                        content_text: null,
                                      },
                                      {
                                        // 適当に被らないであろう ID を振る。
                                        derivation_content_id: Date.now() + 1,
                                        represent: derivationFields.length === 0,
                                        unnecessary: false,
                                        content_xml: null,
                                        content_text: null,
                                      },
                                      {
                                        // 適当に被らないであろう ID を振る。
                                        derivation_content_id: Date.now() + 2,
                                        represent: derivationFields.length === 0,
                                        unnecessary: false,
                                        content_xml: null,
                                        content_text: null,
                                      },
                                    ],
                                    derivation_judgements: Array.isArray(textConditions)
                                      ? textConditions.map(
                                          (psTextCondition: PsTextCondition, psTextConditionIndex: number) => ({
                                            // 適当に被らないであろう ID を振る。
                                            derivation_judgement_id: Date.now() + psTextConditionIndex,
                                            ps_text_condition_id: psTextCondition.text_condition_id,
                                            value: 0,
                                          })
                                        )
                                      : [],
                                  });
                                }}
                              >
                                <i
                                  className="fa-solid fa-circle-plus"
                                  style={{
                                    color: '#3a9f55',
                                  }}
                                />
                                {/* &nbsp;派生追加 */}
                              </span>
                            </div>
                          )}
                      </td>

                      {
                        // ルート PS と章 (L1) PS の場合は非表示。
                        Number(ps.type) > 1 && (
                          // 判断情報ヘッダー。
                          <td
                            colSpan={Array.isArray(textConditions) ? textConditions.length + 2 : 2}
                            className="judgmentbtn"
                            style={{
                              // borderRight: '1px solid #eaeaea',
                              padding: '0',
                            }}
                          >
                            <button
                              type="button"
                              className="text-button"
                              style={{
                                width: '100%',
                                textAlign: 'left',
                                padding: '0 10px',
                              }}
                              onClick={() => {
                                setIsJudgementInfoAreaOpen(!isJudgementInfoAreaOpen);
                              }}
                            >
                              {t('判断情報')}
                              <i
                                className={`fa-solid ${isJudgementInfoAreaOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`}
                                style={{
                                  // position: 'absolute',
                                  // top: '10px',
                                  // right: '10px',
                                  position: 'relative',
                                  paddingLeft: '30px',
                                }}
                              />
                            </button>
                          </td>
                        )
                      }
                    </tr>
                  )
                }

                {
                  // ルート PS と章 (L1) PS の場合は非表示。
                  Number(ps.type) > 1 && (
                    <>
                      {/* SlideToggle は div なので、tr を囲むと子孫がテーブルの配置にならない。各 th や td の中身を SlideToggle で囲むことにする。 */}
                      {/* <SlideToggle
                        open={isJudgementInfoAreaOpen}
                      > */}
                      <tr>
                        <td
                          // rowSpan={(isJudgementInfoAreaOpen) ? 5 : 0}
                          rowSpan={5}
                          style={{
                            width: '21px',
                            padding: isJudgementInfoAreaOpen ? '8px 6px' : '0 6px',
                            borderBottom: '1px solid #eaeaea',
                            borderLeft: '1px solid #eaeaea',
                          }}
                        />
                        <th
                          className="judgementInfoTh"
                          style={{
                            // textAlign: 'left',
                            padding: isJudgementInfoAreaOpen ? '8px 6px' : '0 6px',
                            borderBottom: isJudgementInfoAreaOpen ? '1px solid #eaeaea' : 'none',
                            // borderRight: (isJudgementInfoAreaOpen) ? '1px solid #eaeaea' : 'none',
                          }}
                        >
                          <SlideToggle open={isJudgementInfoAreaOpen}>{t('図面品番')}</SlideToggle>
                        </th>
                        {Array.isArray(textConditions) &&
                          textConditions.map((psTextCondition: PsTextCondition) => (
                            <td
                              onClick={() => {
                                if (mode === 'Diff' && olderPs) {
                                  // 現在の値
                                  const nowValue =
                                    psTextConditionIdDiffTypeMapRef.current.get(
                                      psTextCondition.text_condition_id as number
                                    ) === 'Deleted'
                                      ? ''
                                      : psTextConditionDrawingsToString(psTextCondition.text_condition_drawings);
                                  // 過去の値を取得
                                  let oldValue = '';
                                  const olderPsTextConditionSameUniqueId = getTextConditionRecord(
                                    psTextCondition,
                                    olderPs
                                  );
                                  if (olderPsTextConditionSameUniqueId) {
                                    oldValue = psTextConditionDrawingsToString(
                                      olderPsTextConditionSameUniqueId.text_condition_drawings
                                    );
                                  }
                                  // console.log(oldValue);
                                  // console.log('↓↓↓↓');
                                  // console.log(nowValue);
                                  if (oldValue !== nowValue) {
                                    setDiffTitle('図面品番');
                                    setLanguageId(-1);
                                    setPsValue(nowValue);
                                    setPsOldValue(oldValue);
                                    setIsDiffDlgOpen(true);
                                  }
                                }
                              }}
                              key={psTextCondition.text_condition_id}
                              className="judgementInfoTd"
                              style={{
                                backgroundColor:
                                  psTextConditionIdDiffTypeMapRef.current.get(
                                    psTextCondition.text_condition_id as number
                                  ) === 'Deleted'
                                    ? 'OrangeRed'
                                    : getBackgroundColorValue(
                                        mode,
                                        diffMode,
                                        psTextConditionDrawingsToString(psTextCondition.text_condition_drawings),
                                        getTextConditionRecord(psTextCondition, olderPs)
                                          ? psTextConditionDrawingsToString(
                                              getTextConditionRecord(psTextCondition, olderPs)?.text_condition_drawings
                                            )
                                          : ''
                                      ),
                                padding: isJudgementInfoAreaOpen ? '8px 6px' : '0 6px',
                                borderBottom: isJudgementInfoAreaOpen ? '1px solid #eaeaea' : 'none',
                                borderLeft: isJudgementInfoAreaOpen ? '1px solid #eaeaea' : 'none',
                                // borderRight: (isJudgementInfoAreaOpen) ? '1px solid #eaeaea' : 'none',
                              }}
                            >
                              <SlideToggle open={isJudgementInfoAreaOpen}>
                                {psTextCondition.text_condition_drawings &&
                                  psTextCondition.text_condition_drawings.map(
                                    (psTextConditionDrawing: PsTextConditionDrawing) => (
                                      <React.Fragment key={psTextConditionDrawing.text_condition_drawing_id}>
                                        {/* 表示形式は PS DB を踏襲。 */}
                                        {`${toString(psTextConditionDrawing.dwg_no)}(${toString(
                                          psTextConditionDrawing.supplement
                                        )})`}
                                        <br />
                                      </React.Fragment>
                                    )
                                  )}
                              </SlideToggle>
                            </td>
                          ))}
                      </tr>
                      <tr>
                        <th
                          className="judgementInfoTh"
                          style={{
                            textAlign: 'left',
                            padding: isJudgementInfoAreaOpen ? '8px 6px' : '0 6px',
                            borderBottom: isJudgementInfoAreaOpen ? '1px solid #eaeaea' : 'none',
                            // borderRight: (isJudgementInfoAreaOpen) ? '1px solid #eaeaea' : 'none',
                          }}
                        >
                          <SlideToggle open={isJudgementInfoAreaOpen}>{t('FLIST')}</SlideToggle>
                        </th>
                        {Array.isArray(textConditions) &&
                          textConditions.map((psTextCondition: PsTextCondition) => (
                            <td
                              onClick={() => {
                                if (mode === 'Diff' && olderPs) {
                                  // 現在の値
                                  const nowValue =
                                    psTextConditionIdDiffTypeMapRef.current.get(
                                      psTextCondition.text_condition_id as number
                                    ) === 'Deleted'
                                      ? ''
                                      : psTextConditionFlistsToString(psTextCondition.text_condition_flists);
                                  // 過去の値を取得
                                  let oldValue = '';
                                  const olderPsTextConditionSameUniqueId = getTextConditionRecord(
                                    psTextCondition,
                                    olderPs
                                  );
                                  if (olderPsTextConditionSameUniqueId) {
                                    oldValue = psTextConditionFlistsToString(
                                      olderPsTextConditionSameUniqueId.text_condition_flists
                                    );
                                  }
                                  if (oldValue !== nowValue) {
                                    setDiffTitle('FLIST');
                                    setLanguageId(-1);
                                    setPsValue(nowValue);
                                    setPsOldValue(oldValue);
                                    setIsDiffDlgOpen(true);
                                  }
                                }
                              }}
                              key={psTextCondition.text_condition_id}
                              className="judgementInfoTd"
                              style={{
                                backgroundColor:
                                  psTextConditionIdDiffTypeMapRef.current.get(
                                    psTextCondition.text_condition_id as number
                                  ) === 'Deleted'
                                    ? 'OrangeRed'
                                    : getBackgroundColorValue(
                                        mode,
                                        diffMode,
                                        psTextConditionFlistsToString(psTextCondition.text_condition_flists),
                                        getTextConditionRecord(psTextCondition, olderPs)
                                          ? psTextConditionFlistsToString(
                                              getTextConditionRecord(psTextCondition, olderPs)?.text_condition_flists
                                            )
                                          : ''
                                      ),
                                padding: isJudgementInfoAreaOpen ? '8px 6px' : '0 6px',
                                borderBottom: isJudgementInfoAreaOpen ? '1px solid #eaeaea' : 'none',
                                borderLeft: isJudgementInfoAreaOpen ? '1px solid #eaeaea' : 'none',
                                // borderRight: (isJudgementInfoAreaOpen) ? '1px solid #eaeaea' : 'none',
                              }}
                            >
                              <SlideToggle open={isJudgementInfoAreaOpen}>
                                {psTextCondition.text_condition_flists &&
                                  psTextCondition.text_condition_flists.map(
                                    (psTextConditionFlist: PsTextConditionFlist) => (
                                      <React.Fragment key={psTextConditionFlist.text_condition_flist_id}>
                                        {/* 表示形式は PS DB を踏襲。 */}
                                        {`[${toString(psTextConditionFlist.section)}-${toString(
                                          psTextConditionFlist.feature
                                        )}-${toString(psTextConditionFlist.item)}]`}
                                        <br />
                                      </React.Fragment>
                                    )
                                  )}
                              </SlideToggle>
                            </td>
                          ))}
                      </tr>
                      <tr>
                        <th
                          className="judgementInfoTh"
                          style={{
                            textAlign: 'left',
                            padding: isJudgementInfoAreaOpen ? '8px 6px' : '0 6px',
                            borderBottom: isJudgementInfoAreaOpen ? '1px solid #eaeaea' : 'none',
                            // borderRight: (isJudgementInfoAreaOpen) ? '1px solid #eaeaea' : 'none',
                          }}
                        >
                          <SlideToggle open={isJudgementInfoAreaOpen}>{t('サービススペック表')}</SlideToggle>
                        </th>
                        {Array.isArray(textConditions) &&
                          textConditions.map((psTextCondition: PsTextCondition) => (
                            <td
                              onClick={() => {
                                if (mode === 'Diff' && olderPs) {
                                  // 現在の値
                                  const nowValue =
                                    psTextConditionIdDiffTypeMapRef.current.get(
                                      psTextCondition.text_condition_id as number
                                    ) === 'Deleted'
                                      ? ''
                                      : toString(psTextCondition.service_spec_table);
                                  let oldValue = '';
                                  const olderPsTextConditionSameUniqueId = getTextConditionRecord(
                                    psTextCondition,
                                    olderPs
                                  );
                                  if (olderPsTextConditionSameUniqueId) {
                                    oldValue = toString(olderPsTextConditionSameUniqueId.service_spec_table);
                                  }
                                  if (oldValue !== nowValue) {
                                    setDiffTitle('サービススペック表');
                                    setLanguageId(-1);
                                    setPsValue(nowValue);
                                    setPsOldValue(oldValue);
                                    setIsDiffDlgOpen(true);
                                  }
                                }
                              }}
                              key={psTextCondition.text_condition_id}
                              className="judgementInfoTd"
                              style={{
                                backgroundColor:
                                  psTextConditionIdDiffTypeMapRef.current.get(
                                    psTextCondition.text_condition_id as number
                                  ) === 'Deleted'
                                    ? 'OrangeRed'
                                    : getBackgroundColorValue(
                                        mode,
                                        diffMode,
                                        psTextCondition.service_spec_table,
                                        getTextConditionRecord(psTextCondition, olderPs)
                                          ? getTextConditionRecord(psTextCondition, olderPs)?.service_spec_table
                                          : ''
                                      ),
                                padding: isJudgementInfoAreaOpen ? '8px 6px' : '0 6px',
                                borderBottom: isJudgementInfoAreaOpen ? '1px solid #eaeaea' : 'none',
                                borderLeft: isJudgementInfoAreaOpen ? '1px solid #eaeaea' : 'none',
                                // borderRight: (isJudgementInfoAreaOpen) ? '1px solid #eaeaea' : 'none',
                              }}
                            >
                              <SlideToggle open={isJudgementInfoAreaOpen}>
                                {toString(psTextCondition.service_spec_table)}
                              </SlideToggle>
                            </td>
                          ))}
                      </tr>
                      <tr>
                        <th
                          className="judgementInfoTh"
                          style={{
                            textAlign: 'left',
                            padding: isJudgementInfoAreaOpen ? '8px 6px' : '0 6px',
                            borderBottom: isJudgementInfoAreaOpen ? '1px solid #eaeaea' : 'none',
                            // borderRight: (isJudgementInfoAreaOpen) ? '1px solid #eaeaea' : 'none',
                          }}
                        >
                          <SlideToggle open={isJudgementInfoAreaOpen}>{t('その他トス情報')}</SlideToggle>
                        </th>
                        {Array.isArray(textConditions) &&
                          textConditions.map((psTextCondition: PsTextCondition) => (
                            <td
                              onClick={() => {
                                if (mode === 'Diff' && olderPs) {
                                  // 現在の値
                                  let nowValue = '';
                                  if (
                                    psTextConditionIdDiffTypeMapRef.current.get(
                                      psTextCondition.text_condition_id as number
                                    ) !== 'Deleted'
                                  ) {
                                    nowValue = toString(psTextCondition.tos);
                                    if (
                                      typeof psTextCondition.tos_supplement === 'string' &&
                                      psTextCondition.tos_supplement.length > 0
                                    ) {
                                      nowValue += `(${toString(psTextCondition.tos_supplement)})`;
                                    }
                                  }

                                  // 過去の値を取得
                                  let oldValue = '';
                                  const olderPsTextConditionSameUniqueId = getTextConditionRecord(
                                    psTextCondition,
                                    olderPs
                                  );
                                  if (olderPsTextConditionSameUniqueId) {
                                    // タッチアップ対象
                                    oldValue = toString(olderPsTextConditionSameUniqueId.tos);
                                    if (
                                      typeof olderPsTextConditionSameUniqueId.tos_supplement === 'string' &&
                                      olderPsTextConditionSameUniqueId.tos_supplement.length > 0
                                    ) {
                                      oldValue += `(${toString(olderPsTextConditionSameUniqueId.tos_supplement)})`;
                                    }
                                  }
                                  if (oldValue !== nowValue) {
                                    setDiffTitle('その他トス情報');
                                    setLanguageId(-1);
                                    setPsValue(nowValue);
                                    setPsOldValue(oldValue);
                                    setIsDiffDlgOpen(true);
                                  }
                                }
                              }}
                              key={psTextCondition.text_condition_id}
                              className="judgementInfoTd"
                              style={{
                                backgroundColor:
                                  psTextConditionIdDiffTypeMapRef.current.get(
                                    psTextCondition.text_condition_id as number
                                  ) === 'Deleted'
                                    ? 'OrangeRed'
                                    : getBackgroundColorValue(
                                        mode,
                                        diffMode,
                                        `${toString(psTextCondition.tos)}${toString(psTextCondition.tos_supplement)}`,
                                        getTextConditionRecord(psTextCondition, olderPs)
                                          ? `${toString(
                                              getTextConditionRecord(psTextCondition, olderPs)?.tos
                                            )}${toString(
                                              getTextConditionRecord(psTextCondition, olderPs)?.tos_supplement
                                            )}`
                                          : ''
                                      ),
                                padding: isJudgementInfoAreaOpen ? '8px 6px' : '0 6px',
                                borderBottom: isJudgementInfoAreaOpen ? '1px solid #eaeaea' : 'none',
                                borderLeft: isJudgementInfoAreaOpen ? '1px solid #eaeaea' : 'none',
                                // borderRight: (isJudgementInfoAreaOpen) ? '1px solid #eaeaea' : 'none',
                              }}
                            >
                              <SlideToggle open={isJudgementInfoAreaOpen}>
                                {toString(psTextCondition.tos)}
                                {typeof psTextCondition.tos_supplement === 'string' &&
                                  psTextCondition.tos_supplement.length > 0 &&
                                  `(${toString(psTextCondition.tos_supplement)})`}
                              </SlideToggle>
                            </td>
                          ))}
                      </tr>
                      <tr>
                        <th
                          className="judgementInfoTh"
                          style={{
                            textAlign: 'left',
                            padding: isJudgementInfoAreaOpen ? '8px 6px' : '0 6px',
                            borderBottom: isJudgementInfoAreaOpen ? '1px solid #eaeaea' : 'none',
                            // borderRight: (isJudgementInfoAreaOpen) ? '1px solid #eaeaea' : 'none',
                          }}
                        >
                          <SlideToggle open={isJudgementInfoAreaOpen}>{t('開発部門確認')}</SlideToggle>
                        </th>
                        {Array.isArray(textConditions) &&
                          textConditions.map((psTextCondition: PsTextCondition) => (
                            <td
                              onClick={() => {
                                if (mode === 'Diff' && olderPs) {
                                  // 現在の値
                                  let nowValue = '';
                                  if (
                                    psTextConditionIdDiffTypeMapRef.current.get(
                                      psTextCondition.text_condition_id as number
                                    ) !== 'Deleted'
                                  ) {
                                    if (psTextCondition.hg_check_require !== undefined) {
                                      nowValue = psTextCondition.hg_check_require ? '○' : '×';
                                    }
                                  }
                                  // 過去の値を取得
                                  let oldValue = '';
                                  const olderPsTextConditionSameUniqueId = getTextConditionRecord(
                                    psTextCondition,
                                    olderPs
                                  );
                                  if (olderPsTextConditionSameUniqueId) {
                                    if (olderPsTextConditionSameUniqueId.hg_check_require !== undefined) {
                                      oldValue = olderPsTextConditionSameUniqueId.hg_check_require ? '○' : '×';
                                    }
                                  }
                                  if (oldValue !== nowValue) {
                                    setDiffTitle('開発部門確認');
                                    setLanguageId(-1);
                                    setPsValue(nowValue);
                                    setPsOldValue(oldValue);
                                    setIsDiffDlgOpen(true);
                                  }
                                }
                              }}
                              key={psTextCondition.text_condition_id}
                              className="judgementInfoTd"
                              style={{
                                backgroundColor:
                                  psTextConditionIdDiffTypeMapRef.current.get(
                                    psTextCondition.text_condition_id as number
                                  ) === 'Deleted'
                                    ? 'OrangeRed'
                                    : getBackgroundColorValue(
                                        mode,
                                        diffMode,
                                        psTextCondition.hg_check_require ? '○' : '×',
                                        getTextConditionRecord(psTextCondition, olderPs)
                                          ? getTextConditionRecord(psTextCondition, olderPs)?.hg_check_require
                                            ? '○'
                                            : '×'
                                          : ''
                                      ),
                                padding: isJudgementInfoAreaOpen ? '8px 6px' : '0 6px',
                                borderBottom: isJudgementInfoAreaOpen ? '1px solid #eaeaea' : 'none',
                                borderLeft: isJudgementInfoAreaOpen ? '1px solid #eaeaea' : 'none',
                                // borderRight: (isJudgementInfoAreaOpen) ? '1px solid #eaeaea' : 'none',
                              }}
                            >
                              <SlideToggle open={isJudgementInfoAreaOpen}>
                                {
                                  // PS DB では true (1): 「○」、false (0): PS 閲覧画面ではなにも表示しない、編集画面では「×」となっている。
                                  psTextCondition.hg_check_require ? '○' : isAddingOrEditing ? '×' : ''
                                }
                              </SlideToggle>
                            </td>
                          ))}
                      </tr>
                      <tr>
                        <th
                          colSpan={2}
                          className="judgementInfoTh"
                          style={{
                            textAlign: 'left',
                            padding: isJudgementInfoAreaOpen ? '8px 6px 8px 10px' : 0,
                            borderBottom: isJudgementInfoAreaOpen ? '1px solid #eaeaea' : 'none',
                            borderLeft: isJudgementInfoAreaOpen ? '1px solid #eaeaea' : 'none',
                            // borderRight: (isJudgementInfoAreaOpen) ? '1px solid #eaeaea' : 'none',
                          }}
                        >
                          <SlideToggle open={isJudgementInfoAreaOpen}>{t('テキスト確定条件')}</SlideToggle>
                        </th>
                        {Array.isArray(textConditions) &&
                          textConditions.map((psTextCondition: PsTextCondition) => (
                            <td
                              onClick={() => {
                                if (mode === 'Diff' && olderPs) {
                                  // 現在の値
                                  const nowValue =
                                    psTextConditionIdDiffTypeMapRef.current.get(
                                      psTextCondition.text_condition_id as number
                                    ) === 'Deleted'
                                      ? ''
                                      : psTextCondition.text_condition;
                                  // 過去の値を取得
                                  let oldValue = '';
                                  const olderPsTextConditionSameUniqueId = getTextConditionRecord(
                                    psTextCondition,
                                    olderPs
                                  );
                                  if (olderPsTextConditionSameUniqueId) {
                                    oldValue = olderPsTextConditionSameUniqueId.text_condition ?? '';
                                  }
                                  if (oldValue !== nowValue) {
                                    setDiffTitle('テキスト確定条件');
                                    setLanguageId(-1);
                                    setPsValue(nowValue);
                                    setPsOldValue(oldValue);
                                    setIsDiffDlgOpen(true);
                                  }
                                }
                              }}
                              key={psTextCondition.text_condition_id}
                              className="judgementInfoTd"
                              style={{
                                backgroundColor:
                                  psTextConditionIdDiffTypeMapRef.current.get(
                                    psTextCondition.text_condition_id as number
                                  ) === 'Deleted'
                                    ? 'OrangeRed'
                                    : getBackgroundColorValue(
                                        mode,
                                        diffMode,
                                        psTextCondition.text_condition,
                                        getTextConditionRecord(psTextCondition, olderPs)
                                          ? getTextConditionRecord(psTextCondition, olderPs)?.text_condition
                                          : ''
                                      ),
                                padding: isJudgementInfoAreaOpen ? '8px 6px' : '0 6px',
                                borderBottom: isJudgementInfoAreaOpen ? '1px solid #eaeaea' : 'none',
                                borderLeft: isJudgementInfoAreaOpen ? '1px solid #eaeaea' : 'none',
                                // borderRight: (isJudgementInfoAreaOpen) ? '1px solid #eaeaea' : 'none',
                              }}
                            >
                              <SlideToggle open={isJudgementInfoAreaOpen}>{psTextCondition.text_condition}</SlideToggle>
                            </td>
                          ))}
                      </tr>
                      {isAddingOrEditing && (
                        <tr className="tbicon">
                          <td
                            className="pseditlist"
                            colSpan={2}
                            style={{
                              padding: '6px',
                              border: 'none',
                            }}
                          >
                            <SlideToggle open={isJudgementInfoAreaOpen}>
                              <ul>
                                <li
                                  className="modal-open"
                                  onClick={() => {
                                    if (Array.isArray(textConditions)) {
                                      // テキスト確定条件追加編集ダイアログを表示する。
                                      // 新規追加モード。
                                      setPsTextConditionAddEditDialogOpenModeAndIndex(`Add:${textConditions.length}`);
                                    }
                                  }}
                                >
                                  <i className="fa-solid fa-circle-plus" />
                                  <span>{t('Add')}</span>
                                </li>{' '}
                                <li
                                  onClick={() => {
                                    // localStorage からコピーしていたデータを取得する。
                                    const psTextConditionToPaste = localStorage.getItem(
                                      'PsDetails.PsTextCondition.Copy'
                                    );

                                    if (psTextConditionToPaste) {
                                      // コピーしたテキスト確定条件を列の一番左に挿入する。
                                      insertTextCondition(0, {
                                        ...(JSON.parse(psTextConditionToPaste) as PsTextCondition),
                                        // 次に使用可能な ID を設定。
                                        text_condition_id: getNextId(textConditionIds),
                                        // unique_id を空にする。
                                        unique_id: null,
                                      });
                                    }
                                  }}
                                >
                                  <i className="fa-solid fa-paste" />
                                  <span>{t('Paste')}</span>
                                </li>{' '}
                              </ul>
                            </SlideToggle>
                          </td>
                          {Array.isArray(textConditions) &&
                            textConditions.map((psTextCondition: PsTextCondition, psTextConditionIndex: number) => (
                              <td
                                key={psTextCondition.text_condition_id}
                                className="pseditlist2"
                                style={{
                                  padding: isJudgementInfoAreaOpen ? '6px' : '0',
                                  border: 'none',
                                }}
                              >
                                <SlideToggle open={isJudgementInfoAreaOpen}>
                                  <div className="pseditlist">
                                    <ul>
                                      <li
                                        className="modal-open"
                                        onClick={() => {
                                          // テキスト確定条件追加編集ダイアログを表示する。
                                          // 編集モード。
                                          setPsTextConditionAddEditDialogOpenModeAndIndex(
                                            `Edit:${psTextConditionIndex}`
                                          );
                                        }}
                                      >
                                        <i className="fa-solid fa-pen-to-square" />
                                        <span>{t('Edit')}</span>
                                      </li>{' '}
                                      <li
                                        onClick={() => {
                                          // テキスト確定条件を削除するダイアログを表示。
                                          setPsTextConditionIndexToRemove(psTextConditionIndex);
                                        }}
                                      >
                                        <i className="fa-solid fa-circle-xmark" />
                                        <span>{t('Delete')}</span>
                                      </li>{' '}
                                      <li
                                        onClick={() => {
                                          // 他画面・タブで開いている同画面でもペースト出来るようにする。

                                          // localStorage にコピーするデータを保存する。
                                          localStorage.setItem(
                                            'PsDetails.PsTextCondition.Copy',
                                            JSON.stringify(psTextCondition)
                                          );
                                        }}
                                      >
                                        <i className="fa-solid fa-copy" />
                                        <span>{t('Copy')}</span>
                                      </li>{' '}
                                      <li
                                        onClick={() => {
                                          // localStorage からコピーしていたデータを取得する。
                                          const psTextConditionToPaste = localStorage.getItem(
                                            'PsDetails.PsTextCondition.Copy'
                                          );

                                          if (psTextConditionToPaste) {
                                            // コピーしたテキスト確定条件をこの列の右に挿入する。
                                            insertTextCondition(psTextConditionIndex + 1, {
                                              ...(JSON.parse(psTextConditionToPaste) as PsTextCondition),
                                              // 次に使用可能な ID を設定。
                                              text_condition_id: getNextId(textConditionIds),
                                              // unique_id を空にする。
                                              unique_id: null,
                                            });
                                          }
                                        }}
                                      >
                                        <i className="fa-solid fa-paste" />
                                        <span>{t('Paste')}</span>
                                      </li>
                                    </ul>
                                  </div>
                                </SlideToggle>
                              </td>
                            ))}
                        </tr>
                      )}
                      {/* </SlideToggle> */}
                    </>
                  )
                }
              </tbody>
            </table>
          </div>

          {
            // 「派生文書のみ表示」ボタンが押下されたら非表示にする。
            !doShowDerivationsOnly &&
              // 章 (L1) PS の場合 (ルート PS も同様) は、非表示 (登録不可)。
              Number(ps.type) > 1 && (
                <>
                  <h3 className="mdh2 ft15">
                    <button
                      id="anker-touchup"
                      type="button"
                      className="text-button"
                      onClick={() => {
                        setIsTouchupInfoAreaOpen(!isTouchupInfoAreaOpen);
                      }}
                    >
                      {t('タッチアップ情報')}
                      <i className={`fa-solid ${isTouchupInfoAreaOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
                    </button>
                  </h3>
                  <SlideToggle open={isTouchupInfoAreaOpen}>
                    <div style={{ marginBottom: '20px' }}>
                      {isAddingOrEditing && (
                        <div className="touchup-add">
                          <ul>
                            <li
                              className="modal-open"
                              onClick={() => {
                                if (Array.isArray(touchups)) {
                                  // タッチアップ追加編集ダイアログを表示する。
                                  // 新規追加モード。
                                  setPsTouchupAddEditDialogOpenModeAndIndex(`Add:${touchups.length}`);
                                }
                              }}
                            >
                              <i className="fa-solid fa-circle-plus" />
                              {t('タッチアップ追加')}
                            </li>
                          </ul>
                        </div>
                      )}
                      <table className="table31">
                        <tbody>
                          <tr>
                            <th>No.</th>
                            <th />
                            {/* 表示言語切替 (日本語、米語、英語) チェックボックスの状態に応じ、言語の表示 (チェック時) / 非表示 (非チェック時) を切り替える。 */}
                            {[t('日本語'), t('米語'), t('英語')].map(
                              (language: string, languageIndex: number) =>
                                displayLanguages[languageIndex] && <th key={language}>{language}</th>
                            )}
                            <th />
                            <th />
                            {isAddingOrEditing && <th />}
                          </tr>
                          {Array.isArray(touchups) &&
                            touchups.map((psTouchup: PsTouchup, psTouchupIndex: number) => (
                              <React.Fragment key={psTouchup.touchup_id}>
                                <tr>
                                  <td rowSpan={2}>{psTouchupIndex + 1}</td>
                                  <th>{t('タッチアップ対象')}</th>
                                  {Array.isArray(psTouchup.touchup_targets) &&
                                    psTouchup.touchup_targets.map(
                                      (psTouchupTarget: PsTouchupTarget, psTouchupTargetIndex: number) =>
                                        // 表示言語切替 (日本語、米語、英語) チェックボックスの状態に応じ、言語の表示 (チェック時) / 非表示 (非チェック時) を切り替える。
                                        displayLanguages[psTouchupTargetIndex] && (
                                          <td
                                            onClick={() => {
                                              if (mode === 'Diff' && olderPs) {
                                                const nowValue =
                                                  psTouchupTargetIdTargetDiffTypeMapRef.current.get(
                                                    Number(psTouchupTarget.touchup_target_id)
                                                  ) === 'Deleted'
                                                    ? ''
                                                    : psTouchupTarget.target; // 現在の値
                                                let oldValue = ''; // 過去の値を取得
                                                const oldPsTouchupSameUniqueId = (olderPs.touchups as PsTouchup[]).find(
                                                  (oldPsTouchup: PsTouchup) =>
                                                    oldPsTouchup.unique_id === psTouchup.unique_id
                                                );
                                                if (
                                                  oldPsTouchupSameUniqueId &&
                                                  oldPsTouchupSameUniqueId.touchup_targets
                                                ) {
                                                  // タッチアップ対象
                                                  oldValue =
                                                    oldPsTouchupSameUniqueId.touchup_targets[psTouchupTargetIndex]
                                                      .target ?? '';
                                                }
                                                setDiffTitle('タッチアップ対象');
                                                setLanguageId(psTouchupTargetIndex);
                                                setPsValue(nowValue);
                                                setPsOldValue(oldValue);
                                                setIsDiffDlgOpen(true);
                                              }
                                            }}
                                            key={psTouchupTarget.touchup_target_id}
                                            style={{
                                              // 比較。
                                              backgroundColor: getDiffBackgroundColor(
                                                psTouchupTargetIdTargetDiffTypeMapRef.current,
                                                psTouchupTarget.touchup_target_id as number
                                              ),
                                            }}
                                          >
                                            {psTouchupTarget.target}
                                          </td>
                                        )
                                    )}
                                  {
                                    // touchup_targets は必須ではない。
                                    (!Array.isArray(psTouchup.touchup_targets) ||
                                      psTouchup.touchup_targets.length < 3) &&
                                      // 日米英の言語分、<td /> を生成。
                                      [t('日本語'), t('米語'), t('英語')].map(
                                        (language: string, languageIndex: number) =>
                                          displayLanguages[languageIndex] && <td key={language} />
                                      )
                                  }
                                  <th>{t('タッチアップ')}</th>
                                  <td
                                    onClick={() => {
                                      if (mode === 'Diff' && olderPs) {
                                        // 現在の値(削除の場合は空)
                                        const nowValue =
                                          psTouchupIdTouchUpDiffTypeMapRef.current.get(Number(psTouchup.touchup_id)) ===
                                          'Deleted'
                                            ? ''
                                            : psTouchup.touch_up;
                                        // 過去の値を取得
                                        let oldValue = '';
                                        /** olderPs.touchups の中から、同じ unique_id を持つものを取得。 */
                                        const oldPsTouchupSameUniqueId = (olderPs.touchups as PsTouchup[]).find(
                                          (oldPsTouchup: PsTouchup) => oldPsTouchup.unique_id === psTouchup.unique_id
                                        );
                                        if (oldPsTouchupSameUniqueId) {
                                          // タッチアップ対象
                                          oldValue = oldPsTouchupSameUniqueId.touch_up ?? '';
                                        }
                                        setDiffTitle('タッチアップ');
                                        setLanguageId(-1);
                                        setPsValue(nowValue);
                                        setPsOldValue(oldValue);
                                        setIsDiffDlgOpen(true);
                                      }
                                    }}
                                    style={{
                                      // 比較。
                                      backgroundColor: getDiffBackgroundColor(
                                        psTouchupIdTouchUpDiffTypeMapRef.current,
                                        psTouchup.touchup_id as number
                                      ),
                                    }}
                                  >
                                    {psTouchup.touch_up}
                                  </td>
                                  {isAddingOrEditing && (
                                    <td rowSpan={2}>
                                      <div className="touchuplist">
                                        <ul>
                                          <li
                                            className="modal-open"
                                            onClick={() => {
                                              // タッチアップ追加編集ダイアログを表示する。
                                              // 編集モード。
                                              setPsTouchupAddEditDialogOpenModeAndIndex(`Edit:${psTouchupIndex}`);
                                            }}
                                          >
                                            <i className="fa-solid fa-pen-to-square" />
                                            <span>{t('Edit')}</span>
                                          </li>
                                          <li
                                            onClick={() => {
                                              // タッチアップを削除するダイアログを表示。
                                              setPsTouchupIndexToRemove(psTouchupIndex);
                                            }}
                                          >
                                            <i className="fa-solid fa-circle-xmark" />
                                            <span>{t('Delete')}</span>
                                          </li>
                                        </ul>
                                      </div>
                                    </td>
                                  )}
                                </tr>
                                <tr>
                                  <th style={{ textAlign: 'left' }}>{t('書換単語候補')}</th>
                                  {Array.isArray(psTouchup.touchup_targets) &&
                                    psTouchup.touchup_targets.map(
                                      (psTouchupTarget: PsTouchupTarget, psTouchupTargetIndex: number) =>
                                        // 表示言語切替 (日本語、米語、英語) チェックボックスの状態に応じ、言語の表示 (チェック時) / 非表示 (非チェック時) を切り替える。
                                        displayLanguages[psTouchupTargetIndex] &&
                                        (Array.isArray(psTouchupTarget.touchup_candidates) ? (
                                          <td
                                            onClick={() => {
                                              if (mode === 'Diff' && olderPs) {
                                                // 現在の値(削除の場合は空)
                                                const nowValue =
                                                  psTouchupTargetIdTouchupCandidatesDiffTypeMapRef.current.get(
                                                    Number(psTouchupTarget.touchup_target_id)
                                                  ) === 'Deleted'
                                                    ? ''
                                                    : touchupCandidatesToString(psTouchupTarget.touchup_candidates);
                                                // 過去の値を取得
                                                let oldValue = '';
                                                const oldPsTouchupSameUniqueId = (olderPs.touchups as PsTouchup[]).find(
                                                  (oldPsTouchup: PsTouchup) =>
                                                    oldPsTouchup.unique_id === psTouchup.unique_id
                                                );
                                                if (
                                                  oldPsTouchupSameUniqueId &&
                                                  oldPsTouchupSameUniqueId.touchup_targets
                                                ) {
                                                  // 書換単語候補
                                                  const oldTouchupCandidates =
                                                    oldPsTouchupSameUniqueId.touchup_targets[psTouchupTargetIndex]
                                                      .touchup_candidates;
                                                  oldValue = touchupCandidatesToString(oldTouchupCandidates);
                                                }
                                                setDiffTitle('書換単語候補');
                                                setLanguageId(psTouchupTargetIndex);
                                                setPsValue(nowValue);
                                                setPsOldValue(oldValue);
                                                setIsDiffDlgOpen(true);
                                              }
                                            }}
                                            key={psTouchupTarget.touchup_target_id}
                                            style={{
                                              // 比較。
                                              backgroundColor: getDiffBackgroundColor(
                                                psTouchupTargetIdTouchupCandidatesDiffTypeMapRef.current,
                                                psTouchupTarget.touchup_target_id as number
                                              ),
                                            }}
                                          >
                                            {touchupCandidatesToString(psTouchupTarget.touchup_candidates)}
                                          </td>
                                        ) : undefined)
                                    )}
                                  {
                                    // touchup_targets は必須ではない。
                                    (!Array.isArray(psTouchup.touchup_targets) ||
                                      psTouchup.touchup_targets.length < 3) &&
                                      // 日米英の言語分、<td /> を生成。
                                      [t('日本語'), t('米語'), t('英語')].map(
                                        (language: string, languageIndex: number) =>
                                          displayLanguages[languageIndex] && <td key={language} />
                                      )
                                  }
                                  <th>{t('判断情報')}</th>
                                  <td
                                    onClick={() => {
                                      if (mode === 'Diff' && olderPs) {
                                        // 現在の値
                                        const nowValue =
                                          psTouchupIdJudgementInformationDiffTypeMapRef.current.get(
                                            Number(psTouchup.touchup_id)
                                          ) === 'Deleted'
                                            ? ''
                                            : psTouchup.judgement_information;
                                        // 過去の値を取得
                                        let oldValue = '';
                                        const oldPsTouchupSameUniqueId = (olderPs.touchups as PsTouchup[]).find(
                                          (oldPsTouchup: PsTouchup) => oldPsTouchup.unique_id === psTouchup.unique_id
                                        );
                                        if (oldPsTouchupSameUniqueId) {
                                          // タッチアップ対象
                                          oldValue = oldPsTouchupSameUniqueId.judgement_information ?? '';
                                        }
                                        setDiffTitle('タッチアップ対象');
                                        setLanguageId(-1);
                                        setPsValue(nowValue);
                                        setPsOldValue(oldValue);
                                        setIsDiffDlgOpen(true);
                                      }
                                    }}
                                    style={{
                                      // 比較。
                                      backgroundColor: getDiffBackgroundColor(
                                        psTouchupIdJudgementInformationDiffTypeMapRef.current,
                                        psTouchup.touchup_id as number
                                      ),
                                    }}
                                  >
                                    {psTouchup.judgement_information}
                                  </td>
                                </tr>
                              </React.Fragment>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </SlideToggle>
                  <h3 className="mdh2 ft15">
                    <button
                      id="anker-management"
                      type="button"
                      className="text-button"
                      onClick={() => {
                        setIsManagementInfoAreaOpen(!isManagementInfoAreaOpen);
                      }}
                    >
                      {t('管理情報')}
                      <i className={`fa-solid ${isManagementInfoAreaOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
                    </button>
                  </h3>
                  <SlideToggle open={isManagementInfoAreaOpen}>
                    <div
                      // id='master2'
                      // className='masterfr'
                      style={{
                        marginBottom: '20px',
                      }}
                    >
                      <table className="table32 mb10">
                        <tbody>
                          <tr>
                            <th>{t('記載が必要な理由')}</th>
                            <td
                              onClick={() => {
                                if (mode === 'Diff' && olderPs?.description_reason !== ps.description_reason) {
                                  setDiffTitle('記載が必要な理由');
                                  setLanguageId(-1);
                                  setPsValue(ps.description_reason);
                                  setPsOldValue(olderPs?.description_reason);
                                  setIsDiffDlgOpen(true);
                                }
                              }}
                              style={{
                                backgroundColor: getBackgroundColorValue(
                                  mode,
                                  diffMode,
                                  ps.description_reason ?? '',
                                  olderPs?.description_reason ? olderPs.description_reason : ''
                                ),
                              }}
                            >
                              {isAddingOrEditing ? (
                                <TextInput
                                  name={'description_reason' as never}
                                  register={register}
                                  errors={formState.errors}
                                />
                              ) : (
                                ps.description_reason
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      <table className="table32">
                        <tbody>
                          <tr>
                            <th>{t('HM内情報共有欄')}</th>
                            <td
                              onClick={() => {
                                if (mode === 'Diff' && olderPs?.hm_share !== ps.hm_share) {
                                  setDiffTitle('`HM内情報共有欄');
                                  setLanguageId(-1);
                                  setPsValue(ps.hm_share);
                                  setPsOldValue(olderPs?.hm_share);
                                  setIsDiffDlgOpen(true);
                                }
                              }}
                              style={{
                                backgroundColor: getBackgroundColorValue(
                                  mode,
                                  diffMode,
                                  ps.hm_share ?? '',
                                  olderPs?.hm_share ? olderPs.hm_share : ''
                                ),
                              }}
                            >
                              {isAddingOrEditing ? (
                                <TextInput name={'hm_share' as never} register={register} errors={formState.errors} />
                              ) : (
                                ps.hm_share
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </SlideToggle>

                  <h3 className="mdh2 ft15">
                    <button
                      id="anker-legal"
                      type="button"
                      className="text-button"
                      onClick={() => {
                        setIsLegalInfoAreaOpen(!isLegalInfoAreaOpen);
                      }}
                    >
                      {t('法規情報')}
                      <i className={`fa-solid ${isLegalInfoAreaOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
                    </button>
                  </h3>
                  <SlideToggle open={isLegalInfoAreaOpen}>
                    <div style={{ marginBottom: '20px' }}>
                      <table className="table33">
                        <thead>
                          <tr>
                            <th />
                            {
                              // 表示言語切替 (日本語、米語、英語) チェックボックスの状態に応じ、言語の表示 (チェック時) / 非表示 (非チェック時) を切り替える。
                              [t('日本語'), t('米語'), t('英語')].map(
                                (language: string, languageIndex: number) =>
                                  displayLanguages[languageIndex] && <th key={language}>{language}</th>
                              )
                            }
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <th>{t('法規(verbatim)である')}</th>
                            {Array.isArray(legalInformations) &&
                              legalInformations.map(
                                (psLegalInformation: PsLegalInformation, psLegalInformationIndex: number) =>
                                  // 表示言語切替 (日本語、米語、英語) チェックボックスの状態に応じ、言語の表示 (チェック時) / 非表示 (非チェック時) を切り替える。
                                  displayLanguages[psLegalInformationIndex] && (
                                    <td
                                      onClick={() => {
                                        if (
                                          mode === 'Diff' &&
                                          olderPs?.legal_informations &&
                                          olderPs.legal_informations[psLegalInformationIndex].verbatim !==
                                            psLegalInformation.verbatim
                                        ) {
                                          setDiffTitle('法規(verbatim)である');
                                          setLanguageId(psLegalInformationIndex);
                                          setPsValue(psLegalInformation.verbatim ? '○' : '×');
                                          setPsOldValue(
                                            olderPs?.legal_informations &&
                                              olderPs.legal_informations[psLegalInformationIndex].verbatim
                                              ? '○'
                                              : '×'
                                          );
                                          setIsDiffDlgOpen(true);
                                        }
                                      }}
                                      key={psLegalInformation.legal_information_id}
                                      style={{
                                        backgroundColor: getBackgroundColorValue(
                                          mode,
                                          diffMode,
                                          psLegalInformation.verbatim ? '○' : '×',
                                          olderPs?.legal_informations &&
                                            olderPs.legal_informations[psLegalInformationIndex].verbatim
                                            ? '○'
                                            : '×'
                                        ),
                                      }}
                                    >
                                      {isAddingOrEditing ? (
                                        <CheckBox
                                          name={`legal_informations.${psLegalInformationIndex}.verbatim` as never}
                                          register={register}
                                        />
                                      ) : // PS DB では true (1): 「○」、false(0): PS 閲覧画面では「×」。
                                      psLegalInformation.verbatim ? (
                                        '○'
                                      ) : (
                                        '×'
                                      )}
                                    </td>
                                  )
                              )}
                          </tr>
                          <tr>
                            <th>{t('Verbatimタグ')}</th>
                            {Array.isArray(legalInformations) &&
                              legalInformations.map(
                                (psLegalInformation: PsLegalInformation, psLegalInformationIndex: number) =>
                                  // 表示言語切替 (日本語、米語、英語) チェックボックスの状態に応じ、言語の表示 (チェック時) / 非表示 (非チェック時) を切り替える。
                                  displayLanguages[psLegalInformationIndex] && (
                                    <td
                                      onClick={() => {
                                        if (
                                          mode === 'Diff' &&
                                          olderPs?.legal_informations &&
                                          olderPs.legal_informations[psLegalInformationIndex].verbatim_tag !==
                                            psLegalInformation.verbatim_tag
                                        ) {
                                          setDiffTitle('Verbatimタグ');
                                          setLanguageId(psLegalInformationIndex);
                                          setPsValue(psLegalInformation.verbatim_tag ? '○' : '×');
                                          setPsOldValue(
                                            olderPs?.legal_informations &&
                                              olderPs.legal_informations[psLegalInformationIndex].verbatim_tag
                                              ? '○'
                                              : '×'
                                          );
                                          setIsDiffDlgOpen(true);
                                        }
                                      }}
                                      key={psLegalInformation.legal_information_id}
                                      style={{
                                        backgroundColor: getBackgroundColorValue(
                                          mode,
                                          diffMode,
                                          psLegalInformation.verbatim_tag ? '○' : '×',
                                          olderPs?.legal_informations
                                            ? olderPs.legal_informations[psLegalInformationIndex].verbatim_tag
                                              ? '○'
                                              : '×'
                                            : ''
                                        ),
                                      }}
                                    >
                                      {isAddingOrEditing ? (
                                        <CheckBox
                                          name={`legal_informations.${psLegalInformationIndex}.verbatim_tag` as never}
                                          register={register}
                                        />
                                      ) : // PS DB では true (1): 「○」、false(0): PS 閲覧画面では「×」。
                                      psLegalInformation.verbatim_tag ? (
                                        '○'
                                      ) : (
                                        '×'
                                      )}
                                    </td>
                                  )
                              )}
                          </tr>
                          <tr>
                            <th>{t('法規番号')}</th>
                            {Array.isArray(legalInformations) &&
                              legalInformations.map(
                                (psLegalInformation: PsLegalInformation, psLegalInformationIndex: number) =>
                                  // 表示言語切替 (日本語、米語、英語) チェックボックスの状態に応じ、言語の表示 (チェック時) / 非表示 (非チェック時) を切り替える。
                                  displayLanguages[psLegalInformationIndex] && (
                                    <td
                                      onClick={() => {
                                        if (
                                          mode === 'Diff' &&
                                          olderPs?.legal_informations &&
                                          olderPs.legal_informations[psLegalInformationIndex].information !==
                                            psLegalInformation.information
                                        ) {
                                          setDiffTitle('法規番号');
                                          setLanguageId(psLegalInformationIndex);
                                          setPsValue(psLegalInformation.information);
                                          setPsOldValue(
                                            olderPs?.legal_informations
                                              ? olderPs.legal_informations[psLegalInformationIndex].information
                                              : ''
                                          );
                                          setIsDiffDlgOpen(true);
                                        }
                                      }}
                                      key={psLegalInformation.legal_information_id}
                                      style={{
                                        backgroundColor: getBackgroundColorValue(
                                          mode,
                                          diffMode,
                                          psLegalInformation.information ?? '',
                                          olderPs?.legal_informations
                                            ? olderPs.legal_informations[psLegalInformationIndex].information
                                            : ''
                                        ),
                                      }}
                                    >
                                      {isAddingOrEditing ? (
                                        <TextInput
                                          name={`legal_informations.${psLegalInformationIndex}.information` as never}
                                          register={register}
                                          errors={formState.errors}
                                        />
                                      ) : (
                                        psLegalInformation.information
                                      )}
                                    </td>
                                  )
                              )}
                          </tr>
                          <tr>
                            <th>{t('法規適用国')}</th>
                            {Array.isArray(legalInformations) &&
                              legalInformations.map(
                                (psLegalInformation: PsLegalInformation, psLegalInformationIndex: number) =>
                                  // 表示言語切替 (日本語、米語、英語) チェックボックスの状態に応じ、言語の表示 (チェック時) / 非表示 (非チェック時) を切り替える。
                                  displayLanguages[psLegalInformationIndex] && (
                                    <td
                                      onClick={() => {
                                        if (
                                          mode === 'Diff' &&
                                          olderPs?.legal_informations &&
                                          olderPs.legal_informations[psLegalInformationIndex].legal_regions &&
                                          // 表示用 string で比較。
                                          (
                                            olderPs.legal_informations[psLegalInformationIndex]
                                              .legal_regions as PsLegalRegion[]
                                          )
                                            .map((psLegalRegion: PsLegalRegion) =>
                                              toString(psLegalRegion.legal_region_name)
                                            )
                                            .join() !==
                                            legalRegionsArray[psLegalInformationIndex]
                                              .map((psLegalRegion: PsLegalRegion) =>
                                                toString(psLegalRegion.legal_region_name)
                                              )
                                              .join()
                                        ) {
                                          setDiffTitle('法規適用国');
                                          setLanguageId(psLegalInformationIndex);
                                          setPsValue(
                                            legalRegionsArray[psLegalInformationIndex]
                                              .map((psLegalRegion: PsLegalRegion) =>
                                                toString(psLegalRegion.legal_region_name)
                                              )
                                              .join()
                                          );
                                          setPsOldValue(
                                            olderPs?.legal_informations
                                              ? (
                                                  olderPs.legal_informations[psLegalInformationIndex]
                                                    .legal_regions as PsLegalRegion[]
                                                )
                                                  .map((psLegalRegion: PsLegalRegion) =>
                                                    toString(psLegalRegion.legal_region_name)
                                                  )
                                                  .join()
                                              : ''
                                          );
                                          setIsDiffDlgOpen(true);
                                        }
                                      }}
                                      key={psLegalInformation.legal_information_id}
                                      style={{
                                        backgroundColor: getBackgroundColorValue(
                                          mode,
                                          diffMode,
                                          legalRegionsArray[psLegalInformationIndex]
                                            .map((psLegalRegion: PsLegalRegion) =>
                                              toString(psLegalRegion.legal_region_name)
                                            )
                                            .join(),
                                          olderPs?.legal_informations &&
                                            olderPs.legal_informations[psLegalInformationIndex].legal_regions
                                            ? (
                                                olderPs.legal_informations[psLegalInformationIndex]
                                                  .legal_regions as PsLegalRegion[]
                                              )
                                                .map((psLegalRegion: PsLegalRegion) =>
                                                  toString(psLegalRegion.legal_region_name)
                                                )
                                                .join()
                                            : ''
                                        ),
                                      }}
                                    >
                                      {
                                        // 複数ある場合はカンマ区切りで表示。
                                        legalRegionsArray[psLegalInformationIndex]
                                          .map((psLegalRegion: PsLegalRegion) =>
                                            toString(psLegalRegion.legal_region_name)
                                          )
                                          .join()
                                      }
                                      {/* {
                                        psLegalInformation.legal_regions
                                        &&
                                        psLegalInformation.legal_regions
                                          .map((psLegalRegion: PsLegalRegion) => psLegalRegion.legal_region_name)
                                          .join()
                                      } */}
                                      {isAddingOrEditing && (
                                        <>
                                          <span
                                            className="modal-open htk"
                                            // data-modal="popup25.html"
                                            onClick={() => {
                                              // 法規適用国指定ダイアログを開く。
                                              setPsLegalRegionLanguageId(psLegalInformationIndex);
                                            }}
                                          >
                                            <i className="fa-solid fa-pen-to-square" />
                                          </span>
                                          {t('法規適用国指定')}
                                        </>
                                      )}
                                    </td>
                                  )
                              )}
                          </tr>
                          <tr>
                            <th>{t('法規メモ')}</th>
                            {Array.isArray(legalInformations) &&
                              legalInformations.map(
                                (psLegalInformation: PsLegalInformation, psLegalInformationIndex: number) =>
                                  // 表示言語切替 (日本語、米語、英語) チェックボックスの状態に応じ、言語の表示 (チェック時) / 非表示 (非チェック時) を切り替える。
                                  displayLanguages[psLegalInformationIndex] && (
                                    <td
                                      onClick={() => {
                                        if (
                                          mode === 'Diff' &&
                                          olderPs?.legal_informations &&
                                          olderPs.legal_informations[psLegalInformationIndex].note !==
                                            psLegalInformation.note
                                        ) {
                                          setDiffTitle('法規メモ');
                                          setLanguageId(psLegalInformationIndex);
                                          setPsValue(psLegalInformation.note);
                                          setPsOldValue(
                                            olderPs?.legal_informations
                                              ? olderPs.legal_informations[psLegalInformationIndex].note
                                              : ''
                                          );
                                          setIsDiffDlgOpen(true);
                                        }
                                      }}
                                      key={psLegalInformation.legal_information_id}
                                      style={{
                                        backgroundColor: getBackgroundColorValue(
                                          mode,
                                          diffMode,
                                          psLegalInformation.note ? psLegalInformation.note : '',
                                          olderPs?.legal_informations
                                            ? olderPs.legal_informations[psLegalInformationIndex].note
                                            : ''
                                        ),
                                      }}
                                    >
                                      {isAddingOrEditing ? (
                                        <TextInput
                                          name={`legal_informations.${psLegalInformationIndex}.note` as never}
                                          register={register}
                                          errors={formState.errors}
                                        />
                                      ) : (
                                        psLegalInformation.note
                                      )}
                                    </td>
                                  )
                              )}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </SlideToggle>

                  <h3 className="mdh2 ft15">
                    <button
                      id="anker-other-om"
                      type="button"
                      className="text-button"
                      onClick={() => {
                        setIsOtherMediaInfoAreaOpen(!isOtherMediaInfoAreaOpen);
                      }}
                    >
                      {t('他媒体情報')}
                      <i className={`fa-solid ${isOtherMediaInfoAreaOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
                    </button>
                  </h3>
                  <SlideToggle open={isOtherMediaInfoAreaOpen}>
                    <div style={{ marginBottom: '20px' }}>
                      <table className="table33">
                        <thead>
                          <tr>
                            <th />
                            {
                              // 表示言語切替 (日本語、米語、英語) チェックボックスの状態に応じ、言語の表示 (チェック時) / 非表示 (非チェック時) を切り替える。
                              [t('日本語'), t('米語'), t('英語')].map(
                                (language: string, languageIndex: number) =>
                                  displayLanguages[languageIndex] && <th key={language}>{language}</th>
                              )
                            }
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <th>{t('WebOM記載')}</th>
                            {[0, 1, 2].map(
                              (webOmDescriptionIndex: number) =>
                                // 表示言語切替 (日本語、米語、英語) チェックボックスの状態に応じ、言語の表示 (チェック時) / 非表示 (非チェック時) を切り替える。
                                displayLanguages[webOmDescriptionIndex] && (
                                  <td
                                    onClick={() => {
                                      if (
                                        mode === 'Diff' &&
                                        olderPs?.webom_descriptions &&
                                        olderPs.webom_descriptions[webOmDescriptionIndex] !==
                                          getValues(`webom_descriptions.${webOmDescriptionIndex}` as never)
                                      ) {
                                        setDiffTitle('WebOM記載');
                                        setLanguageId(webOmDescriptionIndex);
                                        setPsValue(getValues(`webom_descriptions.${webOmDescriptionIndex}` as never));
                                        setPsOldValue(
                                          olderPs?.webom_descriptions
                                            ? olderPs.webom_descriptions[webOmDescriptionIndex]
                                            : ''
                                        );
                                        setIsDiffDlgOpen(true);
                                      }
                                    }}
                                    key={webOmDescriptionIndex.toString()}
                                    style={{
                                      backgroundColor: getBackgroundColorValue(
                                        mode,
                                        diffMode,
                                        getValues(`webom_descriptions.${webOmDescriptionIndex}` as never),
                                        olderPs?.webom_descriptions
                                          ? olderPs.webom_descriptions[webOmDescriptionIndex]
                                          : ''
                                      ),
                                    }}
                                  >
                                    {isAddingOrEditing ? (
                                      <TextInput
                                        name={`webom_descriptions.${webOmDescriptionIndex}` as never}
                                        register={register}
                                        errors={formState.errors}
                                      />
                                    ) : (
                                      getValues(`webom_descriptions.${webOmDescriptionIndex}` as never)
                                    )}
                                  </td>
                                )
                            )}
                          </tr>
                          <tr>
                            <th>{t('dOM記載')}</th>
                            {[0, 1, 2].map(
                              (dOmDescriptionIndex: number) =>
                                // 表示言語切替 (日本語、米語、英語) チェックボックスの状態に応じ、言語の表示 (チェック時) / 非表示 (非チェック時) を切り替える。
                                displayLanguages[dOmDescriptionIndex] && (
                                  <td
                                    onClick={() => {
                                      if (
                                        mode === 'Diff' &&
                                        olderPs?.dom_descriptions &&
                                        olderPs.dom_descriptions[dOmDescriptionIndex] !==
                                          getValues(`dom_descriptions.${dOmDescriptionIndex}` as never)
                                      ) {
                                        setDiffTitle('dOM記載');
                                        setLanguageId(dOmDescriptionIndex);
                                        setPsValue(getValues(`dom_descriptions.${dOmDescriptionIndex}` as never));
                                        setPsOldValue(
                                          olderPs?.dom_descriptions ? olderPs.dom_descriptions[dOmDescriptionIndex] : ''
                                        );
                                        setIsDiffDlgOpen(true);
                                      }
                                    }}
                                    key={dOmDescriptionIndex.toString()}
                                    style={{
                                      backgroundColor: getBackgroundColorValue(
                                        mode,
                                        diffMode,
                                        getValues(`dom_descriptions.${dOmDescriptionIndex}` as never),
                                        olderPs?.dom_descriptions ? olderPs.dom_descriptions[dOmDescriptionIndex] : ''
                                      ),
                                    }}
                                  >
                                    {isAddingOrEditing ? (
                                      <TextInput
                                        name={`dom_descriptions.${dOmDescriptionIndex}` as never}
                                        register={register}
                                        errors={formState.errors}
                                      />
                                    ) : (
                                      getValues(`dom_descriptions.${dOmDescriptionIndex}` as never)
                                    )}
                                  </td>
                                )
                            )}
                          </tr>
                          <tr>
                            <th>{t('dOM検索キーワード')}</th>
                            {[0, 1, 2].map(
                              (dOmSearchKeywordIndex: number) =>
                                // 表示言語切替 (日本語、米語、英語) チェックボックスの状態に応じ、言語の表示 (チェック時) / 非表示 (非チェック時) を切り替える。
                                displayLanguages[dOmSearchKeywordIndex] && (
                                  <td
                                    onClick={() => {
                                      if (
                                        mode === 'Diff' &&
                                        olderPs?.dom_search_keywords &&
                                        olderPs.dom_search_keywords[dOmSearchKeywordIndex] !==
                                          getValues(`dom_search_keywords.${dOmSearchKeywordIndex}` as never)
                                      ) {
                                        setDiffTitle('dOM検索キーワード');
                                        setLanguageId(dOmSearchKeywordIndex);
                                        setPsValue(getValues(`dom_search_keywords.${dOmSearchKeywordIndex}` as never));
                                        setPsOldValue(
                                          olderPs?.dom_search_keywords
                                            ? olderPs.dom_search_keywords[dOmSearchKeywordIndex]
                                            : ''
                                        );
                                        setIsDiffDlgOpen(true);
                                      }
                                    }}
                                    key={dOmSearchKeywordIndex.toString()}
                                    style={{
                                      backgroundColor: getBackgroundColorValue(
                                        mode,
                                        diffMode,
                                        getValues(`dom_search_keywords.${dOmSearchKeywordIndex}` as never),
                                        olderPs?.dom_search_keywords
                                          ? olderPs.dom_search_keywords[dOmSearchKeywordIndex]
                                          : ''
                                      ),
                                    }}
                                  >
                                    {isAddingOrEditing ? (
                                      <TextInput
                                        name={`dom_search_keywords.${dOmSearchKeywordIndex}` as never}
                                        register={register}
                                        errors={formState.errors}
                                      />
                                    ) : (
                                      getValues(`dom_search_keywords.${dOmSearchKeywordIndex}` as never)
                                    )}
                                  </td>
                                )
                            )}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </SlideToggle>
                </>
              )
          }

          {
            // 「派生文書のみ表示」ボタンが押下されたら非表示にする。
            !doShowDerivationsOnly &&
              // PS 閲覧画面でのみ表示。
              mode === 'View' && (
                <>
                  <h3 className="mdh2 ft15">
                    <button
                      type="button"
                      className="text-button"
                      onClick={() => {
                        setIsVersionsInfoAreaOpen(!isVersionsInfoAreaOpen);
                      }}
                    >
                      {t('履歴情報')}
                      <i className={`fa-solid ${isVersionsInfoAreaOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
                    </button>
                  </h3>
                  <SlideToggle open={isVersionsInfoAreaOpen}>
                    <div
                      // className='masterfr'
                      style={{
                        marginBottom: '20px',
                      }}
                    >
                      <table className="table34">
                        <thead>
                          <tr>
                            <th>{t('バージョン番号')}</th>
                            <th>{t('変更の理由／内容詳細')}</th>
                            <th>{t('公開日時')}</th>
                            <th>{t('変更打上部門')}</th>
                            <th>{t('作成者')}</th>
                            <th>{t('確認者')}</th>
                            <th>{t('承認者')}</th>
                            <th>{t('承認履歴')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ps.ps_version_histories &&
                            ps.ps_version_histories.map((psVersionHistory: PsVersionHistory) => (
                              <tr key={psVersionHistory.ps_version_id}>
                                <th>
                                  {!psInfoTabMode ? (
                                    <Link
                                      to={
                                        psVersionHistory.ps_version_id
                                          ? `/${i18n.language}/ps/view/${psVersionHistory.ps_version_id}`
                                          : ''
                                      }
                                    >
                                      {psVersionHistory.ps_version_no}
                                    </Link>
                                  ) : (
                                    psVersionHistory.ps_version_no
                                  )}
                                </th>
                                <td>{psVersionHistory.release_note}</td>
                                <td>
                                  {psVersionHistory.release_at && psVersionHistory.release_at.replaceAll('-', '/')}
                                </td>
                                <td>{psVersionHistory.change_request_section}</td>
                                <td>{psVersionHistory.create_user_name}</td>
                                <td>{psVersionHistory.check_group_leader_user_name}</td>
                                <td>{psVersionHistory.approve_manager_user_name}</td>
                                <td>
                                  {psVersionHistory.ps_version_id && (
                                    <span
                                      // data-modal="popup20.html"
                                      // className="modal-open"
                                      onClick={() => {
                                        if (psVersionHistory.ps_version_id) {
                                          setPsStateHistoryDialogVersionId(psVersionHistory.ps_version_id);
                                        }
                                      }}
                                    >
                                      <i className="fa-solid fa-list-ul" />
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </SlideToggle>
                </>
              )
          }
        </div>

        {/* テキスト確定条件編集ダイアログ。 */}
        <PsTextConditionAddEditDialog
          isOpen={Boolean(psTextConditionAddEditDialogOpenModeAndIndex)}
          setIsOpen={(newIsOpen: boolean) => {
            if (!newIsOpen) {
              // 閉じる。
              setPsTextConditionAddEditDialogOpenModeAndIndex(undefined);
            }
          }}
          mode={
            typeof psTextConditionAddEditDialogOpenModeAndIndex === 'string' &&
            psTextConditionAddEditDialogOpenModeAndIndex.split(':')[0] === 'Edit'
              ? 'Edit'
              : 'Add'
          }
          psTextConditionIndex={
            typeof psTextConditionAddEditDialogOpenModeAndIndex === 'string'
              ? Number(psTextConditionAddEditDialogOpenModeAndIndex.split(':')[1])
              : -1
          }
        />

        {psTouchupAddEditDialogOpenModeAndIndex && (
          // タッチアップ追加編集ダイアログ。
          <PsTouchupAddEditDialog
            isOpen={Boolean(psTouchupAddEditDialogOpenModeAndIndex)}
            setIsOpen={(newIsOpen: boolean) => {
              if (!newIsOpen) {
                // 閉じる。
                setPsTouchupAddEditDialogOpenModeAndIndex(undefined);
              }
            }}
            mode={
              typeof psTouchupAddEditDialogOpenModeAndIndex === 'string' &&
              psTouchupAddEditDialogOpenModeAndIndex.split(':')[0] === 'Edit'
                ? 'Edit'
                : 'Add'
            }
            psTouchupIndex={
              typeof psTouchupAddEditDialogOpenModeAndIndex === 'string'
                ? Number(psTouchupAddEditDialogOpenModeAndIndex.split(':')[1])
                : -1
            }
          />
        )}
      </FormProvider>

      {/* 法規適用国指定ダイアログ。 */}
      <PsLegalRegionSelectDialog
        isOpen={psLegalRegionLanguageId >= 0}
        setIsOpen={(newIsOpen: boolean) => {
          if (!newIsOpen) {
            setPsLegalRegionLanguageId(-1);
          }
        }}
        selectedLegalRegionIds={
          psLegalRegionLanguageId >= 0
            ? legalRegionsArray[psLegalRegionLanguageId].map((legalRegion: LegalRegion) =>
                Number(legalRegion.legal_region_id)
              )
            : []
        }
        setLegalRegions={(newLegalRegions: LegalRegion[]) => {
          // setValue で指定された言語の legal_information の legal_regions を設定。
          setValue(`legal_informations.${psLegalRegionLanguageId}.legal_regions` as never, newLegalRegions as never);
        }}
      />

      {/* PS 状態履歴ダイアログ。 */}
      {psStateHistoryDialogVersionId >= 0 && (
        <PsStateHistoryDialog
          onClose={() => {
            // ダイアログを閉じる。
            setPsStateHistoryDialogVersionId(-1);
          }}
          versionId={psStateHistoryDialogVersionId}
          setRefresh={() => undefined}
        />
      )}

      {
        // PS 新規追加 / 更新中、SpinnerModal を表示。
        (postPsAddMutation.isLoading || postPsEditMutation.isLoading) && <SpinnerModal />
      }

      {
        // PS 新規追加 / 更新成功メッセージを表示。
        (postPsAddMutation.isSuccess || postPsEditMutation.isSuccess) && (
          <OkModal
            onClickOk={() => {
              if (postPsAddMutation.isSuccess) {
                // PS 追加画面で改廃案保存成功時 (PS 追加後)、PS 閲覧画面へ遷移する。
                navigate(`/${i18n.language}/ps/view/${toString(postPsAddMutation.data?.data?.ps_version_id)}`);
              }
              if (postPsEditMutation.isSuccess) {
                if (postPsEditMutation.data?.data) {
                  if (postPsEditMutation.data?.data.level !== undefined && postPsEditMutation.data?.data.level >= 2) {
                    reset(getDefaultValues(postPsEditMutation.data.data));
                  } else {
                    // PS編集画面（L0,L1のみ)で改廃案保存成功時、PS閲覧画面へ遷移する。
                    navigate(`/${i18n.language}/ps/view/${toString(postPsEditMutation.data?.data?.ps_version_id)}`);
                  }
                }

                // // PS 編集画面で改廃案保存成功時、返り値の PS の PS バージョン ID に対応する PS 編集画面へ遷移する。
                // navigate(`/${i18n.language}/ps/edit/${toString(postPsEditMutation.data?.data?.ps_version_id)}`);
              }
            }}
            doCloseOnClickOkButton
          >
            {/* {`${t('PSバージョンID:')} ${String(postPsAddMutation.data?.data.ps_version_id)} ${t('の改廃案保存が完了しました。')}`} */}
            <Trans>
              PSバージョンID:{' '}
              {{
                psVersionId: toString(
                  postPsAddMutation.isSuccess
                    ? postPsAddMutation.data?.data.ps_version_id
                    : postPsEditMutation.isSuccess
                    ? postPsEditMutation.data?.data.ps_version_id
                    : ''
                ),
              }}{' '}
              の改廃案保存が完了しました。
            </Trans>
            {/* <Trans
              defaults='PSバージョンID: {{psVersionId}} の改廃案保存が完了しました。'
              values={{ psVersionId: toString(postPsAddMutation.data?.data.ps_version_id), }}
            /> */}
          </OkModal>
        )
      }

      {
        // PS 新規追加エラー メッセージを表示。
        postPsAddMutation.isError && (
          <ErrorModal error={postPsAddMutation.error}>
            {t('PS情報の追加に失敗しました。管理者に打ち上げください。')}
            <br />
            <br />
          </ErrorModal>
        )
      }

      {
        // PS 更新エラー メッセージを表示。
        postPsEditMutation.isError && (
          <ErrorModal error={postPsEditMutation.error}>
            {t('PS情報の保存に失敗しました。管理者に打ち上げください。')}
            <br />
            <br />
          </ErrorModal>
        )
      }

      {
        // handleSubmit() でエラーがあった場合。
        handleSubmitErrors && (
          <OkModal
            onClickOk={() => {
              // handleSubmitErrors をクリアー。
              setHandleSubmitErrors(undefined);
            }}
          >
            {/* {JSON.stringify(handleSubmitErrors)} */}
            {
              // formState.errors[]?.message
              Object.getOwnPropertyNames(formState.errors).map((propName: string) => {
                let errorMessage = '';
                if (
                  propName === 'touchups' &&
                  formState.errors[propName] != null &&
                  Array.isArray(formState.errors[propName])
                ) {
                  errorMessage = t('タッチアップ情報の判断情報は必須項目です。');
                } else if (propName === 'text_conditions' && Array.isArray(formState.errors[propName])) {
                  errorMessage = t(
                    'テキスト確定条件の判断情報に不正な図面番号があります。図面番号は半角英数字5桁で入力して下さい。'
                  );
                } else {
                  errorMessage = toString(formState.errors[propName]?.message);
                }
                return (
                  <div>
                    {
                      // スキーマ定義時に設定した message を置き換えて表示する。
                      errorMessage === 'ps_no_prefix.required()'
                        ? t('管理No.接頭語は必須項目です。')
                        : errorMessage === 'derivations.min(1)'
                        ? t('派生は最低でも1つは登録されている必要があります。')
                        : errorMessage === 'text_conditions.min(1)'
                        ? t('テキスト確定条件は最低でも1つは登録されている必要があります。')
                        : errorMessage === 'represent.check.ja'
                        ? t('日例は１つ選択されている必要があります。')
                        : errorMessage === 'represent.check.us'
                        ? t('米例は１つ選択されている必要があります。')
                        : errorMessage === 'represent.check.en'
                        ? t('英例は１つ選択されている必要があります。')
                        : errorMessage
                    }
                  </div>
                );
              })
            }
          </OkModal>
        )
      }

      {psDerivationIndexToRemove >= 0 && (
        <ConfirmModal
          onClickOk={() => {
            // 派生を削除。
            removeDerivation(psDerivationIndexToRemove);

            // モーダルを閉じる。
            setPsDerivationIndexToRemove(-1);
          }}
          onClickCancel={() => {
            // モーダルを閉じる。
            setPsDerivationIndexToRemove(-1);
          }}
        >
          <div>{t('派生を削除しますか？')}</div>
        </ConfirmModal>
      )}

      {psTextConditionIndexToRemove >= 0 && (
        <ConfirmModal
          onClickOk={() => {
            // テキスト確定条件を削除。
            removeTextCondition(psTextConditionIndexToRemove);

            // モーダルを閉じる。
            setPsTextConditionIndexToRemove(-1);
          }}
          onClickCancel={() => {
            // モーダルを閉じる。
            setPsTextConditionIndexToRemove(-1);
          }}
        >
          <div>{t('テキスト確定条件を削除しますか？')}</div>
        </ConfirmModal>
      )}

      {psTouchupIndexToRemove >= 0 && (
        <ConfirmModal
          onClickOk={() => {
            // タッチアップを削除。
            removeTouchup(psTouchupIndexToRemove);

            // モーダルを閉じる。
            setPsTouchupIndexToRemove(-1);
          }}
          onClickCancel={() => {
            // モーダルを閉じる。
            setPsTouchupIndexToRemove(-1);
          }}
        >
          <div>{t('タッチアップを削除しますか？')}</div>
        </ConfirmModal>
      )}

      {/* PS比較ダイアログ。 */}
      {isDiffDlgOpen && (
        <PsDiffViewerDialog
          isOpen={isDiffDlgOpen}
          setIsOpen={setIsDiffDlgOpen}
          titleText={diffTitle}
          langugeId={languageId}
          oldText={psOldValue}
          newText={psValue}
          oldTitle="変更前"
          newTitle="変更後"
          isXml={false}
        />
      )}

      {/* {
        (okModalContents)
        &&
        (
          <OkModal
          >
            {okModalContents}
          </OkModal>
        )
      } */}
    </>
  );
}

PsDetails.defaultProps = {
  psInfoTabMode: undefined,
  setSelectedDerivationId: undefined,
  isTargetPs: undefined,
  setTargetPs: undefined,
  setDoRePostPsTreeSearch: undefined,
  // setIsPsLegalRegionSelectDialogOpen: undefined,
  // setPsStateHistoryDialogVersionId: undefined,
  modifiedRootPsTreeItem: undefined,
  olderPs: undefined,
  bulkDisplayLanguages: undefined,
  diffMode: undefined,
};

export default PsDetails;

/** 引数の Object の値が null の properties を delete した Object を返す関数。 */
const getNullPropertiesDeletedObject = (object: object) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const newObject = { ...object } as any;

  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  Object.keys(object).forEach((key: string) => {
    if (newObject[key] === null) {
      delete newObject[key];
    }
  });

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return newObject;
};

/** XML から <img /> の src 属性値: <img src="http://localhost:8000/api/pss/illust/1?expires=1684846855&signature=a976dde2015021658f8db26a674771ed5a2737a7c8969640146be509abbc9f86" /> の、「?」以降を除いた string を返す。 */
const getImgSrcParametersRemovedXml = (xml: string) =>
  xml.replaceAll(
    /<img\b[^>]*>/gi,
    (
      match
      // p,
      // offset,
      // string,
      // groups
    ) => {
      const srcRegExpMatchArray = match.match(/\bsrc=("([^"]*)"|'([^']*)')/i);

      const srcValue =
        Array.isArray(srcRegExpMatchArray) && srcRegExpMatchArray.length >= 3
          ? // [2] か [3] どちらかには入っているはず。
            srcRegExpMatchArray[2] || srcRegExpMatchArray[3]
          : '';

      // console.log('match:', match);
      // console.log('srcValue:', srcValue);

      // 「?」以降を削除。
      const srcValueParametersRemoved = srcValue.replace(/\?[^?]*$/, '');

      // console.log('match.replace(srcValue, srcValueParametersRemoved):', match.replace(srcValue, srcValueParametersRemoved));

      return match.replace(srcValue, srcValueParametersRemoved);
    }
  );

/**
 * PS 比較画面で使用。2 つの PsTextConditionDrawing[] を比較する。同一なら true、異なっていれば false を返す。
 * @param {PsTextConditionDrawing[] | undefined} olderPsTextConditionDrawings
 * @param {PsTextConditionDrawing[] | undefined} newerPsTextConditionDrawings
 * @returns {boolean}
 */
const comparePsTextConditionDrawings = (
  olderPsTextConditionDrawings: PsTextConditionDrawing[] | undefined,
  newerPsTextConditionDrawings: PsTextConditionDrawing[] | undefined
): boolean => {
  if (Array.isArray(olderPsTextConditionDrawings)) {
    if (Array.isArray(newerPsTextConditionDrawings)) {
      if (olderPsTextConditionDrawings.length !== newerPsTextConditionDrawings.length) {
        // length が異なっていれば false。
        return false;
      }
      return olderPsTextConditionDrawings.every(
        (olderPsTextConditionDrawing: PsTextConditionDrawing, olderPsTextConditionDrawingIndex: number) =>
          // 中身を比較。同じ位置のものと比較して、内容がすべて同一であれば true。
          // 「ORIAS_J2_テーブル設計書.xlsx」によると、drawing_id は「改廃案承認される前までは NULL」。
          olderPsTextConditionDrawing.dwg_no ===
            newerPsTextConditionDrawings[olderPsTextConditionDrawingIndex].dwg_no &&
          olderPsTextConditionDrawing.supplement ===
            newerPsTextConditionDrawings[olderPsTextConditionDrawingIndex].supplement
      );
    }
    // olderPsTextConditionDrawings が Array で newerPsTextConditionDrawings が Array じゃなければ false。
    return false;
  }
  // どちらも Array じゃなければ true。
  return !Array.isArray(newerPsTextConditionDrawings);
};

/**
 * ps_text_condition_drawings を表示用の string にして返す関数
 * @param {PsTextConditionDrawing[] | undefined} psTextConditionDrawings
 * @returns {string}
 */
const psTextConditionDrawingsToString = (psTextConditionDrawings: PsTextConditionDrawing[] | undefined): string => {
  if (psTextConditionDrawings) {
    if (psTextConditionDrawings.length > 0) {
      return psTextConditionDrawings
        .map(
          (psTextConditionDrawing: PsTextConditionDrawing) =>
            `${toString(psTextConditionDrawing.dwg_no)}(${toString(psTextConditionDrawing.supplement)})`
        )
        .join();
    }
  }
  return '';
};

/**
 * ps_text_condition_flists を表示用の string にして返す関数
 * @param {PsTextConditionFlist[] | undefined} psTextConditionFlists
 * @returns {string}
 */
const psTextConditionFlistsToString = (psTextConditionFlists: PsTextConditionFlist[] | undefined): string => {
  if (psTextConditionFlists) {
    if (psTextConditionFlists.length > 0) {
      return psTextConditionFlists
        .map(
          (psTextConditionFlist: PsTextConditionFlist) =>
            `[${toString(psTextConditionFlist.section)}-${toString(psTextConditionFlist.feature)}-${toString(
              psTextConditionFlist.item
            )}]`
        )
        .join();
    }
  }
  return '';
};

/**
 * PS 比較画面で使用。2 つの PsTextConditionDrawing[] を比較する。同一なら true、異なっていれば false を返す。
 * @param {PsTextConditionFlist[] | undefined} olderPsTextConditionFlists
 * @param {PsTextConditionFlist[] | undefined} newerPsTextConditionFlists
 * @returns {boolean}
 */
const comparePsTextConditionFlists = (
  olderPsTextConditionFlists: PsTextConditionFlist[] | undefined,
  newerPsTextConditionFlists: PsTextConditionFlist[] | undefined
): boolean => {
  if (Array.isArray(olderPsTextConditionFlists)) {
    if (Array.isArray(newerPsTextConditionFlists)) {
      if (olderPsTextConditionFlists.length !== newerPsTextConditionFlists.length) {
        // length が異なっていれば false。
        return false;
      }
      return olderPsTextConditionFlists.every(
        (olderPsTextConditionFlist: PsTextConditionFlist, olderPsTextConditionFlistIndex: number) =>
          // 中身を比較。同じ位置のものと比較して、内容がすべて同一であれば true。
          olderPsTextConditionFlist.section === newerPsTextConditionFlists[olderPsTextConditionFlistIndex].section &&
          olderPsTextConditionFlist.feature === newerPsTextConditionFlists[olderPsTextConditionFlistIndex].feature &&
          olderPsTextConditionFlist.item === newerPsTextConditionFlists[olderPsTextConditionFlistIndex].item
      );
    }
    // olderPsTextConditionFlists が Array で newerPsTextConditionFlists が Array じゃなければ false。
    return false;
  }
  // どちらも Array じゃなければ true。
  return !Array.isArray(newerPsTextConditionFlists);
};

/**
 * PsTouchupTarget.touchup_candidates を表示用の string にして返す関数。
 * @param {PsTouchupCandidate[] | undefined} psTouchupCandidates
 * @returns {string}
 */
const touchupCandidatesToString = (psTouchupCandidates: PsTouchupCandidate[] | undefined): string => {
  if (Array.isArray(psTouchupCandidates)) {
    return psTouchupCandidates.length > 0
      ? psTouchupCandidates.map((psTouchupCandidate: PsTouchupCandidate) => psTouchupCandidate.candidate).join()
      : '-';
  }

  return '-';
};

/**
 * Diff 用の backgroundColor を返す関数。
 * @param {Map<number, DiffType>} idDiffTypeMap
 * @param {number} id
 * @returns {string}
 */
export const getDiffBackgroundColor = (idDiffTypeMap: Map<number, DiffType>, id: number): string => {
  let backgroundColor = 'inherit';

  // PS 比較画面: PS 詳細情報については、新旧 PS データで結果が異なる部分を、追加・変更された情報は黄色、削除された情報は赤色でハイライト表示する。
  switch (idDiffTypeMap.get(id)) {
    case 'Added':
    case 'Modified':
      backgroundColor = 'Yellow';
      break;

    case 'Deleted':
      backgroundColor = 'OrangeRed';
      break;

    default:
      break;
  }

  return backgroundColor;
};

/**
 * Diff 用の backgroundColor を返す関数。
 * @param mode
 * @param newValue
 * @param oldValue
 * @returns {string}
 */
export const getBackgroundColorValue = (
  mode: PsDetailsMode,
  diffMode: PsDiffMode | undefined,
  newValue: string | undefined,
  oldValue: string | undefined
): string => {
  if (mode === 'Diff' && diffMode === 'Add') {
    // 比較モードが追加の場合は全て黄色を返す
    return 'Yellow';
  }
  if (mode === 'Diff' && oldValue !== undefined) {
    const oldV = oldValue ?? '';
    const newV = newValue ?? '';
    if (oldV !== newV) {
      if (oldV !== '' && newV === '') {
        return 'OrangeRed';
      }
      if (oldV !== newV) {
        return 'Yellow';
      }
    }
  }
  return 'inherit';
};

/**
 * 変更前のテキストコンディションレコード取得
 * @param psTextCondition
 * @param olderPs
 * @returns
 */
export const getTextConditionRecord = (
  psTextCondition: PsTextCondition,
  olderPs: Ps | undefined
): PsTextCondition | undefined => {
  if (olderPs !== undefined) {
    // 前PSの同じレコード
    const olderPsTextConditionSameUniqueId = (olderPs.text_conditions as PsTextCondition[]).find(
      (olderPsTextCondition: PsTextCondition) => olderPsTextCondition.unique_id === psTextCondition.unique_id
    );
    if (olderPsTextConditionSameUniqueId) {
      return olderPsTextConditionSameUniqueId;
    }
  }
  return undefined;
};

/**
 * Diff 用の backgroundColor を返す関数。
 * @param mode
 * @param newValue
 * @param oldValue
 * @returns {string}
 */
export const getBackgroundColorTextCondition = (
  mode: PsDetailsMode,
  newValue: string | undefined,
  oldValue: string | undefined
): string => {
  // console.log(newValue);
  // console.log(oldValue);
  if (mode === 'Diff' && oldValue !== undefined) {
    if (newValue === undefined || newValue === null || newValue === '') {
      return 'OrangeRed';
    }
    if (newValue !== oldValue) {
      return 'Yellow';
    }
  }
  return 'inherit';
};
